import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonControl, DialogControl, FieldControl, IconControl, PanelControl } from "spark-core-dx/components";
import { useGenericManager } from "spark-core-dx/hooks";
import { AuthManager } from "spark-core-dx/managers";
import { SystemTypes } from "spark-core-dx/services";
import { ItemServiceFactory } from "../../../services/ItemServiceFactory";
import { Operations } from "../../../services/ItemServices";
import { sanitizeHtmlFragment } from "../../../utilities/markupSanitizer";
import AIPanel from "../../../components/AIChat/AIPanel";
import ConvokeDialogMessage from "../../../components/convoke/ConvokeDialogMessage";
import ActiveSessionListControl, {
  ActiveSessionDetails,
  activeSessionId,
  activeSessionIsActive,
  activeSessionKind
} from "../../../components/security/ActiveSessionListControl";
import ResourceStateControl from "../components/ResourceStateControl";
import { useResourceDomainContext } from "../ResourceDomainProvider";
import { EndpointRouteIcon } from "./ResourceWorkspaceDestinationScreens";
import { getSchemaDestinationStatusLabel } from "./resourceWorkspace.destinationUtils";
import {
  MCP_TOOLS_ACTIVE_SESSIONS_SCREEN_KEY,
  MCP_TOOLS_AUTH_SCREEN_KEY,
  MCP_TOOLS_CAPABILITIES_SCREEN_KEY,
  MCP_TOOLS_EXPOSURE_SCREEN_KEY,
  MCP_TOOLS_OVERVIEW_SCREEN_KEY,
  MCP_TOOLS_RULES_SCREEN_KEY,
  isClientMcpServerResource
} from "./resourceWorkspace.mcpToolsUtils";

const formatLocalDate = (value) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultActiveSessionOnOrBefore = () => formatLocalDate(new Date());

const getDefaultInactiveSessionOnOrBefore = () => {
  const current = new Date();
  const originalDay = current.getDate();
  current.setDate(1);
  current.setMonth(current.getMonth() - 1);
  const lastDay = new Date(current.getFullYear(), current.getMonth() + 1, 0).getDate();
  current.setDate(Math.min(originalDay, lastDay));
  return formatLocalDate(current);
};

const onOrBeforeDateToUtc = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || "").trim());
  if (!match) return "";
  const localEndOfDay = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    23,
    59,
    59,
    999
  );
  return Number.isNaN(localEndOfDay.getTime()) ? "" : localEndOfDay.toISOString();
};

const CLIENT_SERVER_NAME = "convoke-client-mcp";
const CLIENT_TOOLS_SERVER_NAME = "convoke-tools-mcp";
const PROTOCOL_VERSION = "2025-03-26";
const ROUTE_TEMPLATE = "/mcp/{clientShortName}";
const ADMIN_ROUTE = "/mcp/_admin/V1/tools";
const SYSADMIN_ROUTE = "/mcp/_sysadmin/V1/tools";
const SAVE_CLIENT_MCP_SERVER_SETTINGS_OPERATION = Operations.ResourceConfigurationItem?.SaveClientMcpServerSettings || "SaveClientMcpServerSettings";
const DOWNLOAD_CLIENT_MCP_SERVER_OAUTH_SETUP_SCRIPT_OPERATION = Operations.ResourceConfigurationItem?.DownloadClientMcpServerOAuthSetupScript || "DownloadClientMcpServerOAuthSetupScript";
const TEST_CLIENT_MCP_SERVER_CURRENT_USER_CONNECTION_OPERATION = Operations.ResourceConfigurationItem?.TestClientMcpServerCurrentUserConnection || "TestClientMcpServerCurrentUserConnection";
const CREATE_CLIENT_MCP_SERVER_PAT_OPERATION = Operations.ResourceConfigurationItem?.CreateClientMcpServerPersonalAccessToken || "CreateClientMcpServerPersonalAccessToken";
const REVOKE_CLIENT_MCP_SERVER_PAT_OPERATION = Operations.ResourceConfigurationItem?.RevokeClientMcpServerPersonalAccessToken || "RevokeClientMcpServerPersonalAccessToken";
const LOAD_CLIENT_MCP_SERVER_ACTIVE_SESSIONS_OPERATION = Operations.ResourceConfigurationItem?.LoadClientMcpServerActiveSessions || "LoadClientMcpServerActiveSessions";
const REVOKE_CLIENT_MCP_SERVER_ACTIVE_SESSION_OPERATION = Operations.ResourceConfigurationItem?.RevokeClientMcpServerActiveSession || "RevokeClientMcpServerActiveSession";
const AI_QUESTION_CLIENT_MCP_TOOLS_OPERATION = Operations.AIChatSessionItem?.AIQuestionClientMcpTools || "AIQuestionClientMcpTools";
const AI_QUESTION_CLIENT_MCP_TOOLS_REFERENCE = "ClientMcpTools";
const EMPTY_LOAD_STATE = { status: "idle", error: "" };
const CLIENT_SERVER_AUTH_OAUTH = 1;
const CLIENT_SERVER_AUTH_PAT = 2;
const CLIENT_SERVER_AUTH_OAUTH_AND_PAT = 3;
const CONFIGURATION_STATUS_LABELS = {
  1: "Draft",
  2: "Configuring",
  3: "Pending Review",
  4: "Active",
  5: "Failed",
  6: "Retired",
  7: "Configured",
  8: "Ready",
  9: "Disabled"
};

const getValue = (item, ...keys) => {
  for (const key of keys) {
    const value = item?.[key];
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
};

const getString = (item, ...keys) => String(getValue(item, ...keys) ?? "").trim();

const extractPatSecretDialogResult = (value) => {
  const text = String(value || "");
  const clipboardMatch = text.match(/<\|CLIPBOARD="([^"]+)"\|>/i);
  let copyValue = "";

  if (clipboardMatch?.[1]) {
    try {
      const normalized = clipboardMatch[1].replace(/-/g, "+").replace(/_/g, "/");
      copyValue = atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "="));
    } catch {
      copyValue = "";
    }
  }

  return {
    copyValue,
    html: text
      ? sanitizeHtmlFragment(text.replace(/<\|CLIPBOARD="[^"]+"\|>/ig, '<span class="api-token-created__copy-marker">Use Copy PAT Secret</span>'))
      : ""
  };
};

const getArray = (item, ...keys) => {
  const value = getValue(item, ...keys);
  return Array.isArray(value) ? value : [];
};

const getId = (item) => getString(item, "_id", "id", "Id", "UniqueId", "uniqueId");

const getBoolean = (item, ...keys) => {
  const value = getValue(item, ...keys);
  return value === true || value === 1 || String(value ?? "").trim().toLowerCase() === "true";
};

const getOperationResponse = (result) => (Array.isArray(result) ? result[0] : result) || {};

const operationResponseFailed = (result) => {
  const response = getOperationResponse(result);
  const message = getValue(response, "Message", "message") || {};
  return getValue(response, "Success", "success") === false
    || getBoolean(message, "IsFailMessage", "isFailMessage");
};

const getOperationFailureMessage = (result, fallbackMessage) => {
  const response = getOperationResponse(result);
  const message = getValue(response, "Message", "message") || {};
  return getString(response, "DisplayMessage", "displayMessage")
    || getString(message, "DisplayMessage", "displayMessage", "Content", "content")
    || getArray(response, "MessageDetails", "messageDetails").map((detail) => String(detail || "").trim()).filter(Boolean)[0]
    || fallbackMessage;
};

const getCustomFields = (item) => getValue(item, "CustomFields", "customFields") || {};

const getCustomField = (item, ...keys) => {
  const customFields = getCustomFields(item);
  for (const key of keys) {
    const value = customFields?.[key] ?? customFields?.[`${key.charAt(0).toLowerCase()}${key.slice(1)}`];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return "";
};

const formatDateTime = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const normalizeRoutePart = (value) =>
  String(value ?? "")
    .trim()
    .replace(/[^A-Za-z0-9_-]+/g, "");

const resolveClientShortName = ({ coreContext, configuration, selectedResource }) => normalizeRoutePart(
  getString(coreContext, "ClientShortCode", "clientShortCode")
  || getCustomField(configuration, "McpToolsClientShortName")
  || getString(selectedResource?.clientResource, "ClientShortName", "clientShortName")
  || getString(coreContext?.Profile, "ClientName", "clientName")
);

const resolveRouteMode = ({ configuration, selectedResource }) => {
  const explicitMode = getCustomField(configuration, "McpToolsRouteMode");
  if (explicitMode) return String(explicitMode).trim().toLowerCase();

  const resourceKey = getString(configuration, "ProductResourceKey", "productResourceKey")
    || getString(selectedResource?.clientResource, "ProductResourceKey", "productResourceKey");
  if (/sysadmin/i.test(resourceKey)) return "sysadmin";
  if (/admin/i.test(resourceKey)) return "admin";
  return "client";
};

const getRouteModeLabel = (routeMode, isClientMcpServer) => {
  if (routeMode === "admin") return "Admin MCP Tools";
  if (routeMode === "sysadmin") return "Dev SysAdmin MCP Tools";
  return isClientMcpServer ? "Client MCP Server" : "Client MCP Tools";
};

const getConfigurationStatusLabel = (configuration) => {
  const status = Number(getValue(configuration, "ConfigurationStatus", "configurationStatus"));
  return CONFIGURATION_STATUS_LABELS[status] || "Unavailable";
};

const normalizeCompare = (value) => String(value ?? "").trim().toLowerCase();

const isLinkedToClientMcpServer = (providerConfiguration, serverConfigurationId) =>
  getArray(providerConfiguration, "LinkedRecords", "linkedRecords").some((record) =>
    normalizeCompare(getString(record, "ItemName", "itemName")) === "resourceconfiguration"
    && normalizeCompare(getString(record, "Relationship", "relationship")) === "clientmcpserver"
    && normalizeCompare(getString(record, "ItemId", "itemId")) === normalizeCompare(serverConfigurationId)
  );

const getLinkedSchemaApiRecord = (providerConfiguration) =>
  getArray(providerConfiguration, "LinkedRecords", "linkedRecords").find((record) =>
    normalizeCompare(getString(record, "ItemName", "itemName")) === "schemaapi"
  ) || null;

const isClientMcpToolsProvider = (providerConfiguration) =>
  ["clientmcptoolsendpoint", "clientmcptools", "convokeclientmcptools"].includes(normalizeCompare(
    getString(providerConfiguration, "ProductResourceKey", "productResourceKey", "ProductKey", "productKey")
  ));

const isMcpQueryDataProvider = (providerConfiguration) =>
  ["mcpdataqueryendpoint", "schemaapi"].includes(normalizeCompare(
    getString(providerConfiguration, "ProductResourceKey", "productResourceKey", "ProductKey", "productKey")
  ));

const getProviderStatusClassName = (status) => {
  const normalized = normalizeCompare(status);
  if (normalized === "online") return "resource-workspace-status-pill--online";
  if (["enabled", "active", "configured", "ready"].includes(normalized)) return "resource-workspace-status-pill--success";
  if (normalized === "offline") return "resource-workspace-status-pill--offline";
  if (["disabled", "retired"].includes(normalized)) return "resource-workspace-status-pill--muted";
  if (normalized === "failed") return "resource-workspace-status-pill--error";
  return "resource-workspace-status-pill--warning";
};

const AUTHORIZED_TOOL_PROVIDER_GROUPS = [
  { key: "server", label: "Client MCP Server", matches: (name) => name.startsWith("mcp_") },
  { key: "tools", label: "Client MCP: Tools", matches: (name) => name.startsWith("client_") },
  { key: "data", label: "Client MCP: Query Data", matches: (name) => name.startsWith("data_") },
  { key: "other", label: "Other capabilities", matches: () => true }
];

const groupAuthorizedMcpTools = (tools) => {
  const groups = AUTHORIZED_TOOL_PROVIDER_GROUPS.map((provider) => ({ ...provider, tools: [] }));
  (Array.isArray(tools) ? tools : []).forEach((tool) => {
    const name = String(tool || "").trim();
    if (!name) return;
    groups.find((group) => group.matches(name))?.tools.push(name);
  });
  return groups.filter((group) => group.tools.length);
};

const getHostedApiBaseUri = () => {
  if (typeof window === "undefined") return "";
  const config = window.configData || window.config || {};
  const apiUris = config.API_URIS || config.apiUris || {};
  const convokeUri = getString(apiUris, "CONVOKE", "Convoke", "convoke")
    || getString(config, "CONVOKE_API_URI", "convokeApiUri");
  return convokeUri.replace(/\/api\/Convoke\/?$/i, "").replace(/\/+$/, "");
};

const buildToolsRoute = (routeMode, clientShortName) => {
  if (routeMode === "admin") return ADMIN_ROUTE;
  if (routeMode === "sysadmin") return SYSADMIN_ROUTE;
  return clientShortName ? `/mcp/${clientShortName}` : ROUTE_TEMPLATE;
};

const buildToolsUrl = (routeMode, clientShortName) => {
  const route = buildToolsRoute(routeMode, clientShortName);
  const baseUri = getHostedApiBaseUri();
  return baseUri ? `${baseUri}${route}` : route;
};

const buildClientMcpConnectorUrls = (routeMode, clientShortName, isClientMcpServer) => {
  if (routeMode !== "client") {
    return [{
      key: routeMode,
      label: getRouteModeLabel(routeMode, isClientMcpServer),
      method: "POST",
      url: buildToolsUrl(routeMode, clientShortName)
    }];
  }

  const clientRoutePart = clientShortName || "{clientShortName}";
  const baseUri = getHostedApiBaseUri();
  return [
    { key: "generic", label: "Generic MCP Connector", route: `/mcp/${clientRoutePart}` },
    { key: "claude", label: "Claude MCP Connector", route: `/mcp-claude/${clientRoutePart}` },
    { key: "chatgpt", label: "ChatGPT MCP Connector", route: `/mcp-chatgpt/${clientRoutePart}` }
  ].map((connector) => ({
    ...connector,
    method: "POST",
    url: baseUri ? `${baseUri}${connector.route}` : connector.route
  }));
};

const getAccessToken = () => {
  try {
    const token = String(AuthManager?.getAccessToken?.() || AuthManager?.getTokens?.()?.Access || "").trim();
    if (token) return token;
    if (typeof window === "undefined") return "";
    return String(JSON.parse(window.sessionStorage?.getItem("CoreAuth") || "null")?.Access || "").trim();
  } catch (ex) {
    return "";
  }
};

const buildRpcPayload = (method, params) => ({
  jsonrpc: "2.0",
  id: `${method}-${Date.now()}`,
  method,
  ...(params ? { params } : {})
});

const readRpcResponse = async (response) => {
  const rawText = await response.text();
  let payload = rawText;
  try {
    payload = rawText ? JSON.parse(rawText) : {};
  } catch (ex) {
    payload = { raw: rawText };
  }
  if (!response.ok) {
    throw new Error(payload?.error?.message || payload?.message || response.statusText || `HTTP ${response.status}`);
  }
  if (payload?.error) {
    throw new Error(payload.error?.data?.detail || payload.error?.message || "MCP JSON-RPC request failed.");
  }
  return payload;
};

const SECRET_KEY_PATTERN = /(password|secret|token|credential|connectionstring|privatekey|securecustomfields|authorization|auth)/i;

const redact = (value, key = "") => {
  if (SECRET_KEY_PATTERN.test(key)) return "[redacted]";
  if (Array.isArray(value)) return value.map((item) => redact(item));
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([entryKey, entryValue]) => [entryKey, redact(entryValue, entryKey)]));
  }
  return value;
};

const prettyJson = (value) => JSON.stringify(redact(value ?? {}), null, 2);

const parseResourceText = (response) => {
  const result = response?.result || response?.Result || {};
  const contents = result?.contents || result?.Contents || response?.contents || response?.Contents || [];
  const text = contents[0]?.text || contents[0]?.Text || "";
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (ex) {
    return { text };
  }
};

const readResourcePayload = (response) => {
  const parsed = parseResourceText(response);
  if (parsed) return parsed;

  const result = response?.result || response?.Result;
  if (!result || result?.error || result?.Error) return null;
  return result;
};

const readCapabilityPayload = (responses) =>
  readResourcePayload(responses.capabilities) || {};

const hasRulePayload = (payload) =>
  Boolean(payload && typeof payload === "object" && Object.keys(payload).length > 0);

const getItems = (result) =>
  getArray(result, "Items", "items", "Data", "data", "Value", "value")
    .concat(Array.isArray(result) ? result : []);

const getOperationResultItem = (result) => {
  const direct = getValue(result, "Item", "item");
  if (direct) return direct;
  const resultItems = getArray(result, "Items", "items");
  if (resultItems.length) return resultItems[0];
  const data = getValue(result, "Data", "data");
  if (data && data !== result) return getOperationResultItem(data);
  return null;
};

const getOperationMessage = (result, fallback) => {
  const message = getValue(result, "Message", "message") || {};
  return getString(result, "DisplayMessage", "displayMessage")
    || getString(message, "DisplayMessage", "displayMessage", "Content", "content")
    || fallback;
};

const getSortDateValue = (item) => {
  const value = getValue(item, "ModifiedDate", "modifiedDate", "UpdatedDate", "updatedDate", "CreatedDate", "createdDate");
  const parsed = Date.parse(String(value ?? ""));
  return Number.isNaN(parsed) ? 0 : parsed;
};

const Fact = ({ label, value }) => (
  <div className="resource-workspace-fact">
    <dt>{label}</dt>
    <dd>{value === undefined || value === null || value === "" ? "Not set" : String(value)}</dd>
  </div>
);

const CLIENT_MCP_GUIDE_PROMPTS = [
  {
    title: "Connect an AI client",
    prompt: "How do I connect this Client MCP Tools server to my AI client, and what sign-in behavior should I expect?"
  },
  {
    title: "Use exposed items",
    prompt: "Which exposed items can I read or change through this Client MCP Tools server, and what guardrails apply?"
  },
  {
    title: "Run operations",
    prompt: "Which operations are available through this Client MCP Tools server, and when should I use run_operation instead of save_item?"
  },
  {
    title: "Handle errors",
    prompt: "Explain the common Client MCP Tools JSON-RPC errors and what I should do to recover safely."
  }
];

const CLIENT_MCP_GLOSSARY_TERMS = [
  {
    term: "MCP URL",
    definition: "The URL an AI client uses to connect to this Client MCP Tools server."
  },
  {
    term: "MCP Tool",
    definition: "A callable action advertised by the server, such as querying exposed items or running exposed operations."
  },
  {
    term: "MCP Metadata URI",
    definition: "A readable documentation or schema resource that explains available tools, rules, and exposed targets."
  },
  {
    term: "Exposed Item",
    definition: "A Convoke item whose metadata allows this route to read or change it, subject to entitlement and normal permissions."
  },
  {
    term: "Exposed Operation",
    definition: "A Convoke operation whose metadata allows this route to execute it, subject to entitlement and normal permissions."
  }
];

const ClientMcpToolsGuidePanel = ({
  routeUrl,
  routeEnabled,
  routeMode,
  clientShortName,
  toolCount,
  metadataUriCount,
  exposedItems,
  exposedOperations,
  lastCheckedDisplay,
  copiedPromptTitle,
  onCopyPrompt
}) => {
  const [activeGuideTab, setActiveGuideTab] = useState("documentation");
  const itemPreview = exposedItems.slice(0, 4).map((item) => getString(item, "displayName", "DisplayName", "itemName", "ItemName")).filter(Boolean);
  const operationPreview = exposedOperations.slice(0, 4).map((item) => getString(item, "displayName", "DisplayName", "operationName", "OperationName")).filter(Boolean);

  return (
    <div className="ai-chat-shell">
      <div className="ai-chat-shell__body ai-scroll">
      <section className="ai-review-card" id="client-mcp-guide-summary">
        <div className="ai-review-card__header">
          <div>
            <span className="ai-review-card__eyebrow">Client route</span>
            <h4>Client MCP Tools Guide</h4>
          </div>
          <span className="ai-review-card__chip">{routeEnabled ? "Enabled" : "Disabled"}</span>
        </div>
        <div className="ai-review-card__section">
          <p>Use this guide to understand the server, available documentation, exposed metadata, and safe AI workflows.</p>
        </div>
        <dl className="resource-workspace-facts">
          <Fact label="Route" value={routeUrl} />
          <Fact label="Status" value={routeEnabled ? "Enabled" : "Disabled"} />
          <Fact label="Route mode" value={routeMode} />
          <Fact label="Client" value={clientShortName || "Not set"} />
        </dl>
      </section>

      <div className="form-tabs-container form-tabs-container-top client-mcp-tools-guide-tabs" role="tablist" aria-label="Client MCP Tools guide tabs">
        <button
          type="button"
          className={`form-tab-rows ${activeGuideTab === "documentation" ? "form-tab-row-active" : ""}`}
          role="tab"
          aria-selected={activeGuideTab === "documentation"}
          aria-controls="client-mcp-guide-tab-panel"
          onClick={() => setActiveGuideTab("documentation")}
        >
          <span className="form-tab">Documentation</span>
        </button>
        <button
          type="button"
          className={`form-tab-rows ${activeGuideTab === "questions" ? "form-tab-row-active" : ""}`}
          role="tab"
          aria-selected={activeGuideTab === "questions"}
          aria-controls="client-mcp-guide-tab-panel"
          onClick={() => setActiveGuideTab("questions")}
        >
          <span className="form-tab">Questions</span>
        </button>
        <button
          type="button"
          className={`form-tab-rows ${activeGuideTab === "glossary" ? "form-tab-row-active" : ""}`}
          role="tab"
          aria-selected={activeGuideTab === "glossary"}
          aria-controls="client-mcp-guide-tab-panel"
          onClick={() => setActiveGuideTab("glossary")}
        >
          <span className="form-tab">Glossary</span>
        </button>
      </div>

      {activeGuideTab === "documentation" && (
      <section className="ai-review-card" id="client-mcp-guide-tab-panel" role="tabpanel">
        <div className="ai-review-card__header">
          <div>
            <span className="ai-review-card__eyebrow">Documentation</span>
            <h4>Documentation Snapshot</h4>
          </div>
        </div>
        <div className="ai-review-card__section">
          <p>These counts come from the cached Client MCP discovery data for this route.</p>
        </div>
        <dl className="resource-workspace-facts">
          <Fact label="Tools" value={toolCount.toLocaleString()} />
          <Fact label="Metadata URIs" value={metadataUriCount.toLocaleString()} />
          <Fact label="Exposed items" value={exposedItems.length.toLocaleString()} />
          <Fact label="Exposed operations" value={exposedOperations.length.toLocaleString()} />
          <Fact label="Last checked" value={lastCheckedDisplay} />
        </dl>
        <div className="resource-workspace-record-list">
          <article className="resource-workspace-record-row">
            <div className="resource-workspace-record-row__copy">
              <strong>Item examples</strong>
              <span>{itemPreview.length ? itemPreview.join(", ") : "No exposed item examples loaded yet."}</span>
            </div>
          </article>
          <article className="resource-workspace-record-row">
            <div className="resource-workspace-record-row__copy">
              <strong>Operation examples</strong>
              <span>{operationPreview.length ? operationPreview.join(", ") : "No exposed operation examples loaded yet."}</span>
            </div>
          </article>
        </div>
      </section>
      )}

      {activeGuideTab === "questions" && (
      <section className="ai-review-card" id="client-mcp-guide-tab-panel" role="tabpanel">
        <div className="ai-review-card__header">
          <div>
            <span className="ai-review-card__eyebrow">Guide prompts</span>
            <h4>Common Questions</h4>
          </div>
        </div>
        <div className="ai-review-card__section">
          <p>Copy a question, paste it into chat, and the assistant will answer from this MCP server context.</p>
        </div>
        <div className="resource-workspace-record-list">
          {CLIENT_MCP_GUIDE_PROMPTS.map((item) => (
            <article className="resource-workspace-record-row" key={item.title}>
              <div className="resource-workspace-record-row__copy">
                <strong>{item.title}</strong>
                <span>{item.prompt}</span>
              </div>
              <div className="resource-workspace-record-row-actions">
                <ButtonControl
                  type="standard-small"
                  onClick={() => onCopyPrompt(item)}
                >
                  {copiedPromptTitle === item.title ? "Copied" : "Copy"}
                </ButtonControl>
              </div>
            </article>
          ))}
        </div>
      </section>
      )}

      {activeGuideTab === "glossary" && (
      <section className="ai-review-card" id="client-mcp-guide-tab-panel" role="tabpanel">
        <div className="ai-review-card__header">
          <div>
            <span className="ai-review-card__eyebrow">Reference</span>
            <h4>Glossary</h4>
          </div>
        </div>
        <div className="ai-review-card__section">
          <p>Key terms used by this Client MCP Tools server.</p>
        </div>
        <div className="resource-workspace-record-list">
          {CLIENT_MCP_GLOSSARY_TERMS.map((item) => (
            <article className="resource-workspace-record-row" key={item.term}>
              <div className="resource-workspace-record-row__copy">
                <strong>{item.term}</strong>
                <span>{item.definition}</span>
              </div>
            </article>
          ))}
        </div>
      </section>
      )}
      </div>
    </div>
  );
};

const getMcpResult = (payload) =>
  getValue(payload, "result", "Result") || {};

const renderInitializeDialog = (details, fallbackServerName) => {
  const result = getMcpResult(details);
  const serverInfo = getValue(result, "serverInfo", "ServerInfo") || {};
  const capabilities = getValue(result, "capabilities", "Capabilities") || {};
  const toolsCapability = getValue(capabilities, "tools", "Tools");
  const resourcesCapability = getValue(capabilities, "resources", "Resources");
  const instructions = getString(result, "instructions", "Instructions");

  return (
    <div className="client-mcp-tools-parsed-dialog">
      <div className="client-mcp-tools-dialog-heading">
        <IconControl iconKey="rule" width="1.4rem" height="1.4rem" />
      </div>
      <p>
        The MCP connection has been validated. This did not create or change server state; it verified that this URL is the Client MCP Server and reported the protocol and capabilities an AI client can use.
      </p>
      <dl className="resource-workspace-facts">
        <Fact label="Server" value={getString(serverInfo, "name", "Name") || fallbackServerName} />
        <Fact label="Protocol" value={getString(result, "protocolVersion", "ProtocolVersion") || PROTOCOL_VERSION} />
        <Fact label="MCP URL" value={getString(details, "routeUrl")} />
        <Fact label="Tools capability" value={toolsCapability ? "Available" : "Not advertised"} />
        <Fact label="Metadata URI capability" value={resourcesCapability ? "Available" : "Not advertised"} />
      </dl>
      {instructions ? (
        <div className="client-mcp-tools-dialog-note">
          {instructions}
        </div>
      ) : null}
    </div>
  );
};

const renderInstallationReadinessDialog = (details) => {
  const checks = [
    ...getArray(details, "connectors").map((connector) => ({
      key: getString(connector, "key", "url"),
      label: getString(connector, "label")
    })),
    { key: "oauth-discovery", label: "OAuth discovery" },
    { key: "authentication-challenge", label: "Authentication challenge" }
  ];

  return (
    <div className="client-mcp-tools-authentication-test-result">
      <div
        className="resource-workspace-access-note resource-workspace-access-note--success"
        role="status"
      >
        Ready to install. Public setup guides, OAuth discovery metadata, and authentication challenges are available.
      </div>
      <section className="client-mcp-tools-authentication-capability-section" aria-label="Installation checks">
        <h3>Installation checks</h3>
        <div className="client-mcp-tools-authentication-capability-grid">
          {checks.map((check) => (
            <article className="client-mcp-tools-authentication-capability-item" key={check.key}>
              <span><strong>{check.label}</strong></span>
              <span className="resource-workspace-status-pill resource-workspace-status-pill--success">
                Ready
              </span>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

const buildClientServerOAuthScopesField = () => ({
  FieldName: "ClientMcpServerOAuthScopes",
  fieldName: "ClientMcpServerOAuthScopes",
  DisplayName: "OAuth Scopes",
  displayName: "OAuth Scopes",
  Description: "Optional space-delimited scopes advertised by the shared Client MCP Server. Leave empty to use the client-server defaults.",
  description: "Optional space-delimited scopes advertised by the shared Client MCP Server. Leave empty to use the client-server defaults.",
  PlaceHolder: "Leave empty to use defaults",
  placeHolder: "Leave empty to use defaults",
  FieldType: SystemTypes.UIFieldType.TextBox,
  fieldType: SystemTypes.UIFieldType.TextBox,
  Format: SystemTypes.UIFormats?.STRING
});

const normalizeClientServerAuthenticationMode = (value) => {
  const numericValue = Number(value);
  return [CLIENT_SERVER_AUTH_OAUTH, CLIENT_SERVER_AUTH_PAT, CLIENT_SERVER_AUTH_OAUTH_AND_PAT].includes(numericValue)
    ? numericValue
    : CLIENT_SERVER_AUTH_OAUTH;
};

const clientServerAuthenticationAllowsOAuth = (mode) => [CLIENT_SERVER_AUTH_OAUTH, CLIENT_SERVER_AUTH_OAUTH_AND_PAT].includes(normalizeClientServerAuthenticationMode(mode));
const clientServerAuthenticationAllowsPat = (mode) => [CLIENT_SERVER_AUTH_PAT, CLIENT_SERVER_AUTH_OAUTH_AND_PAT].includes(normalizeClientServerAuthenticationMode(mode));
const CLIENT_MCP_AUTHENTICATION_TAB_STORAGE_PREFIX = "Convoke.ResourceWorkspace.ClientMcpAuthenticationTab";
const CLIENT_MCP_AUTHENTICATION_TABS = ["oauth", "pat", "test"];

const readClientMcpAuthenticationTab = (storageKey) => {
  if (!storageKey || typeof window === "undefined") return "";
  try {
    const storedTab = window.sessionStorage?.getItem(storageKey) || "";
    return CLIENT_MCP_AUTHENTICATION_TABS.includes(storedTab) ? storedTab : "";
  } catch {
    return "";
  }
};

const writeClientMcpAuthenticationTab = (storageKey, tab) => {
  if (!storageKey || typeof window === "undefined" || !CLIENT_MCP_AUTHENTICATION_TABS.includes(tab)) return;
  try {
    window.sessionStorage?.setItem(storageKey, tab);
  } catch {
    // The selected tab is a non-critical UI preference; storage may be unavailable.
  }
};

const resolveClientMcpAuthenticationTab = (authenticationMode, requestedTab) => {
  if (requestedTab === "test") return "test";
  if (requestedTab === "oauth" && clientServerAuthenticationAllowsOAuth(authenticationMode)) return "oauth";
  if (requestedTab === "pat" && clientServerAuthenticationAllowsPat(authenticationMode)) return "pat";
  if (clientServerAuthenticationAllowsOAuth(authenticationMode)) return "oauth";
  if (clientServerAuthenticationAllowsPat(authenticationMode)) return "pat";
  return "test";
};

const formatClientServerAuthenticationMode = (mode) => ({
  [CLIENT_SERVER_AUTH_OAUTH]: "OAuth",
  [CLIENT_SERVER_AUTH_PAT]: "Personal Access Token",
  [CLIENT_SERVER_AUTH_OAUTH_AND_PAT]: "OAuth + Personal Access Token"
}[normalizeClientServerAuthenticationMode(mode)]);

const buildClientServerAuthenticationModeField = () => ({
  FieldName: "ClientMcpServerAuthenticationMode",
  fieldName: "ClientMcpServerAuthenticationMode",
  DisplayName: "Authentication Mode",
  displayName: "Authentication Mode",
  Description: "Choose how AI clients authenticate to the shared Client MCP Server.",
  description: "Choose how AI clients authenticate to the shared Client MCP Server.",
  FieldType: SystemTypes.UIFieldType.SelectList,
  fieldType: SystemTypes.UIFieldType.SelectList,
  Format: SystemTypes.UIFormats?.INT,
  IsRequired: true,
  DataJson: [
    { Text: "OAuth", Value: CLIENT_SERVER_AUTH_OAUTH },
    { Text: "Personal Access Token", Value: CLIENT_SERVER_AUTH_PAT },
    { Text: "OAuth + Personal Access Token", Value: CLIENT_SERVER_AUTH_OAUTH_AND_PAT }
  ]
});

const buildClientServerPatLabelField = () => ({
  FieldName: "ClientMcpServerPatLabel",
  fieldName: "ClientMcpServerPatLabel",
  DisplayName: "Token Label",
  displayName: "Token Label",
  Description: "Friendly name for this profile-owned token.",
  description: "Friendly name for this profile-owned token.",
  PlaceHolder: "For example: Local Claude testing",
  placeHolder: "For example: Local Claude testing",
  FieldType: SystemTypes.UIFieldType.TextBox,
  fieldType: SystemTypes.UIFieldType.TextBox,
  Format: SystemTypes.UIFormats?.STRING,
  IsRequired: true
});

const buildClientServerPatExpirationField = () => ({
  FieldName: "ClientMcpServerPatExpiration",
  fieldName: "ClientMcpServerPatExpiration",
  DisplayName: "Expiration",
  displayName: "Expiration",
  Description: "The PAT stops working automatically after this duration.",
  description: "The PAT stops working automatically after this duration.",
  FieldType: SystemTypes.UIFieldType.SelectList,
  fieldType: SystemTypes.UIFieldType.SelectList,
  Format: SystemTypes.UIFormats?.STRING,
  IsRequired: true,
  DataJson: [
    { Text: "1 Day", Value: "1d" },
    { Text: "1 Week", Value: "1w" },
    { Text: "1 Month", Value: "1m" },
    { Text: "3 Months", Value: "3m" },
    { Text: "6 Months", Value: "6m" },
    { Text: "1 Year", Value: "1y" }
  ]
});

const buildClientServerTestAuthTypeField = (mode) => ({
  FieldName: "ClientMcpServerTestAuthenticationType",
  fieldName: "ClientMcpServerTestAuthenticationType",
  DisplayName: "Credential Type",
  displayName: "Credential Type",
  FieldType: SystemTypes.UIFieldType.SelectList,
  fieldType: SystemTypes.UIFieldType.SelectList,
  Format: SystemTypes.UIFormats?.STRING,
  IsRequired: true,
  DataJson: [
    { Text: "Current Signed-in User", Value: "current" },
    ...(clientServerAuthenticationAllowsOAuth(mode) ? [{ Text: "OAuth Access Token", Value: "oauth" }] : []),
    ...(clientServerAuthenticationAllowsPat(mode) ? [{ Text: "Personal Access Token", Value: "pat" }] : [])
  ]
});

const buildClientServerTestCredentialField = (credentialType) => ({
  FieldName: "ClientMcpServerTestCredential",
  fieldName: "ClientMcpServerTestCredential",
  DisplayName: credentialType === "pat" ? "Personal Access Token" : "OAuth Access Token",
  displayName: credentialType === "pat" ? "Personal Access Token" : "OAuth Access Token",
  Description: "Used only for this connection test and never saved.",
  description: "Used only for this connection test and never saved.",
  FieldType: SystemTypes.UIFieldType.Password,
  fieldType: SystemTypes.UIFieldType.Password,
  Format: SystemTypes.UIFormats?.STRING,
  IsRequired: true
});

const ClientMcpToolsAuthenticationControl = ({
  authenticationMode,
  busyKey,
  downloadingScript,
  onDownloadEntraSetupScript,
  onCreatePat,
  onDeletePat,
  onRevokePat,
  onTestConnection,
  oauthScopes,
  patExpiration,
  patLabel,
  personalAccessTokens,
  routeEnabled,
  routeMode,
  routeModeLabel,
  routeReady,
  routeUrl,
  setAuthenticationMode,
  setOauthScopes,
  setPatExpiration,
  setPatLabel,
  tabStorageKey
}) => {
  const [activeAuthTab, setActiveAuthTab] = useState(() => resolveClientMcpAuthenticationTab(
    authenticationMode,
    readClientMcpAuthenticationTab(tabStorageKey)
  ));
  const [authModeInfoOpen, setAuthModeInfoOpen] = useState(false);
  const [entraSetupDialogOpen, setEntraSetupDialogOpen] = useState(false);
  const [deletePat, setDeletePat] = useState(null);
  const [revokePat, setRevokePat] = useState(null);
  const restoredTabStorageKey = useRef(tabStorageKey);
  const [testCredentialType, setTestCredentialType] = useState("current");
  const [testCredential, setTestCredential] = useState("");
  const allowsOAuth = clientServerAuthenticationAllowsOAuth(authenticationMode);
  const allowsPat = clientServerAuthenticationAllowsPat(authenticationMode);
  const tokenRows = Array.isArray(personalAccessTokens)
    ? personalAccessTokens.filter((token) => !getBoolean(token, "IsDeleted", "isDeleted"))
    : [];

  useEffect(() => {
    if (testCredentialType === "oauth" && !allowsOAuth) setTestCredentialType("current");
    if (testCredentialType === "pat" && !allowsPat) setTestCredentialType("current");
  }, [allowsOAuth, allowsPat, testCredentialType]);

  useEffect(() => {
    setActiveAuthTab((currentTab) => {
      if (restoredTabStorageKey.current !== tabStorageKey) {
        restoredTabStorageKey.current = tabStorageKey;
        return resolveClientMcpAuthenticationTab(
          authenticationMode,
          readClientMcpAuthenticationTab(tabStorageKey)
        );
      }
      return resolveClientMcpAuthenticationTab(authenticationMode, currentTab);
    });
  }, [authenticationMode, tabStorageKey]);

  useEffect(() => {
    writeClientMcpAuthenticationTab(tabStorageKey, activeAuthTab);
  }, [activeAuthTab, tabStorageKey]);

  return (
    <div className="resource-wizard__linked-step client-mcp-tools-auth-control" style={{ alignSelf: "start", minHeight: "auto" }}>
      <div className="resource-wizard__linked-step-header">
        <div>
          <h2>Authentication</h2>
          <p>Configure and test how AI clients connect to this shared Client MCP Server.</p>
        </div>
      </div>

      <div className="resource-wizard__management-auth-controls" />

      <div className="resource-wizard__review-summary client-mcp-tools-auth-summary">
        <section className="resource-wizard__review-summary-section">
          <div className="resource-wizard__review-summary-records">
            <article className="resource-wizard__review-summary-record">
              <div className="resource-wizard__review-summary-record-facts">
                <div className="resource-wizard__info-pair"><span>Unified connector</span><strong>{routeUrl}</strong></div>
                <div className="resource-wizard__info-pair"><span>Identity boundary</span><strong>Client and user profile</strong></div>
                <div className="resource-wizard__info-pair"><span>Authorization boundary</span><strong>Capability and Data Context</strong></div>
              </div>
            </article>
          </div>
        </section>

        <section className="resource-wizard__review-summary-section client-mcp-tools-auth-mode-section">
          <ButtonControl
            type="icon"
            className="client-mcp-tools-auth-mode-info"
            title="About Authentication Mode"
            aria-label="About Authentication Mode"
            onClick={() => setAuthModeInfoOpen(true)}
          >
            <IconControl iconKey="info" title="About Authentication Mode" width="1rem" height="1rem" />
          </ButtonControl>
          <div className="resource-wizard__field">
            <FieldControl
              className="resource-wizard__field-control"
              fieldMetadata={buildClientServerAuthenticationModeField()}
              value={authenticationMode}
              onChange={(value) => setAuthenticationMode(normalizeClientServerAuthenticationMode(value))}
              disableError
            />
          </div>
        </section>
      </div>

      <div className="resource-wizard__authentication-tab-shell">
        <div className="form-wrapper form-wrapper-tabs-top">
          <div className="form-tabs-container form-tabs-container-top resource-wizard__authentication-tablist" role="tablist" aria-label="Authentication tabs">
            {allowsOAuth ? <button
              id="client-mcp-authentication-oauth-tab"
              type="button"
              className={`form-tab-rows ${activeAuthTab === "oauth" ? "form-tab-row-active" : ""}`}
              role="tab"
              aria-selected={activeAuthTab === "oauth"}
              aria-controls="client-mcp-authentication-tab-panel"
              onClick={() => setActiveAuthTab("oauth")}
            >
              <span className="form-tab">Setup OAuth</span>
            </button> : null}
            {allowsPat ? <button
              id="client-mcp-authentication-pat-tab"
              type="button"
              className={`form-tab-rows ${activeAuthTab === "pat" ? "form-tab-row-active" : ""}`}
              role="tab"
              aria-selected={activeAuthTab === "pat"}
              aria-controls="client-mcp-authentication-tab-panel"
              onClick={() => setActiveAuthTab("pat")}
            >
              <span className="form-tab">Setup PAT</span>
            </button> : null}
            <button
              id="client-mcp-authentication-test-tab"
              type="button"
              className={`form-tab-rows ${activeAuthTab === "test" ? "form-tab-row-active" : ""}`}
              role="tab"
              aria-selected={activeAuthTab === "test"}
              aria-controls="client-mcp-authentication-tab-panel"
              onClick={() => setActiveAuthTab("test")}
            >
              <span className="form-tab">Test</span>
            </button>
          </div>
        </div>

        <div
          id="client-mcp-authentication-tab-panel"
          className="resource-wizard__review-summary"
          role="tabpanel"
          aria-labelledby={`client-mcp-authentication-${activeAuthTab}-tab`}
        >
          {activeAuthTab === "oauth" && allowsOAuth ? (
            <section className="resource-wizard__review-summary-section">
                <div className="resource-wizard__review-summary-section-header">
                  <div>
                    <h4>Shared OAuth Connection</h4>
                    <p className="resource-wizard__review-summary-record-subtitle">
                      Entra issues the user-bound connection token for this server.
                    </p>
                  </div>
                  <div className="resource-wizard__linked-step-actions-right">
                    <ButtonControl
                      type="standard-small"
                      className="resource-wizard__button resource-wizard__button--secondary"
                      disabled={downloadingScript}
                      loading={downloadingScript}
                      onClick={() => setEntraSetupDialogOpen(true)}
                    >
                      Download Entra Setup Script
                    </ButtonControl>
                  </div>
                </div>
                <div className="resource-wizard__field">
                  <FieldControl
                    className="resource-wizard__field-control"
                    fieldMetadata={buildClientServerOAuthScopesField()}
                    value={oauthScopes}
                    onChange={(value) => setOauthScopes(String(value || ""))}
                    disableError
                  />
                </div>
            </section>
          ) : activeAuthTab === "pat" && allowsPat ? (
            <section className="resource-wizard__review-summary-section">
                <div className="resource-wizard__review-summary-section-header">
                  <div>
                    <h4>Personal Access Tokens</h4>
                    <p className="resource-wizard__review-summary-record-subtitle">
                      Create a profile-owned bearer token for local, Claude, Codex, or manual testing. The secret is shown once and stored only as a hash.
                    </p>
                  </div>
                  <div className="resource-wizard__linked-step-actions-right">
                    <ButtonControl
                      type="ok-small"
                      disabled={!String(patLabel || "").trim() || Boolean(busyKey)}
                      loading={busyKey === "createServerPat"}
                      onClick={onCreatePat}
                    >
                      Create PAT
                    </ButtonControl>
                  </div>
                </div>
                <div className="resource-wizard__fields">
                  <div className="resource-wizard__field">
                    <FieldControl
                      className="resource-wizard__field-control"
                      fieldMetadata={buildClientServerPatLabelField()}
                      value={patLabel}
                      onChange={(value) => setPatLabel(String(value || ""))}
                      disableError
                    />
                  </div>
                  <div className="resource-wizard__field">
                    <FieldControl
                      className="resource-wizard__field-control"
                      fieldMetadata={buildClientServerPatExpirationField()}
                      value={patExpiration}
                      onChange={(value) => setPatExpiration(String(value || "1m"))}
                      disableError
                    />
                  </div>
                </div>
                <div className="resource-wizard__review-summary-records">
                  {tokenRows.length ? tokenRows.map((token) => {
                    const tokenId = getString(token, "TokenId", "tokenId");
                    const isActiveValue = getValue(token, "IsActive", "isActive");
                    const revoked = Boolean(getString(token, "RevokedDate", "revokedDate"))
                      || (isActiveValue !== undefined && isActiveValue !== null && !getBoolean(token, "IsActive", "isActive"));
                    return (
                      <article className="resource-wizard__review-summary-record" key={tokenId || getString(token, "TokenFingerprint", "tokenFingerprint")}>
                        <div className="resource-wizard__review-summary-section-header">
                          <div>
                            <h4>{getString(token, "TokenLabel", "tokenLabel") || "Client MCP Server PAT"}</h4>
                            <p className="resource-wizard__review-summary-record-subtitle">
                              {revoked ? "Revoked" : "Active"} · {getString(token, "TokenPrefix", "tokenPrefix")} · fingerprint {getString(token, "TokenFingerprint", "tokenFingerprint")}
                            </p>
                          </div>
                          {!revoked ? <ButtonControl
                            type="icon-small"
                            disabled={Boolean(busyKey)}
                            loading={busyKey === `revokeServerPat:${tokenId}`}
                            title={`Revoke ${getString(token, "TokenLabel", "tokenLabel") || "active PAT"}`}
                            aria-label={`Revoke ${getString(token, "TokenLabel", "tokenLabel") || "active PAT"}`}
                            onClick={() => setRevokePat(token)}
                          >
                            <IconControl
                              iconKey="undo"
                              title={`Revoke ${getString(token, "TokenLabel", "tokenLabel") || "active PAT"}`}
                              width="1rem"
                              height="1rem"
                            />
                          </ButtonControl> : <ButtonControl
                            type="icon-small"
                            disabled={Boolean(busyKey)}
                            loading={busyKey === `deleteServerPat:${tokenId}`}
                            title={`Delete ${getString(token, "TokenLabel", "tokenLabel") || "revoked PAT"}`}
                            aria-label={`Delete ${getString(token, "TokenLabel", "tokenLabel") || "revoked PAT"}`}
                            onClick={() => setDeletePat(token)}
                          >
                            <IconControl
                              iconKey="delete"
                              color="#121212"
                              title={`Delete ${getString(token, "TokenLabel", "tokenLabel") || "revoked PAT"}`}
                              width="1rem"
                              height="1rem"
                            />
                          </ButtonControl>}
                        </div>
                        <div className="resource-wizard__review-summary-record-facts">
                          <div className="resource-wizard__info-pair"><span>Owner</span><strong>{getString(token, "TokenOwnerProfileName", "tokenOwnerProfileName") || getString(token, "TokenOwnerProfileEmail", "tokenOwnerProfileEmail")}</strong></div>
                          <div className="resource-wizard__info-pair"><span>Expires</span><strong>{formatDateTime(getString(token, "ExpirationDate", "expirationDate"))}</strong></div>
                          <div className="resource-wizard__info-pair"><span>Last used</span><strong>{formatDateTime(getString(token, "LastUsedDate", "lastUsedDate"))}</strong></div>
                        </div>
                      </article>
                    );
                  }) : <ResourceStateControl compact title="No personal access tokens" message="Create a PAT above when a client cannot complete the OAuth flow." />}
                </div>
            </section>
          ) : (
            <section className="resource-wizard__review-summary-section">
              <div className="resource-wizard__review-summary-section-header">
                <div>
                  <h4>Authentication Test</h4>
                  <p className="resource-wizard__review-summary-record-subtitle">
                    Use the current signed-in user, an OAuth access token, or a PAT to initialize the server and verify tool and deployment discovery. Entered credentials are never saved.
                  </p>
                </div>
                <ButtonControl
                  type="ok-small"
                  disabled={!routeReady || !routeEnabled || (testCredentialType !== "current" && !testCredential.trim()) || Boolean(busyKey)}
                  loading={busyKey === "testServerAuthentication"}
                  onClick={() => onTestConnection({ credentialType: testCredentialType, credential: testCredential.trim() })}
                >
                  Test Connection
                </ButtonControl>
              </div>
              <div className="resource-wizard__fields">
                <div className="resource-wizard__field">
                  <FieldControl
                    className="resource-wizard__field-control"
                    fieldMetadata={buildClientServerTestAuthTypeField(authenticationMode)}
                    value={testCredentialType}
                    onChange={(value) => setTestCredentialType(String(value || "oauth"))}
                    disableError
                  />
                </div>
                {testCredentialType !== "current" ? <div className="resource-wizard__field">
                  <FieldControl
                    className="resource-wizard__field-control"
                    fieldMetadata={buildClientServerTestCredentialField(testCredentialType)}
                    value={testCredential}
                    onChange={(value) => setTestCredential(String(value || ""))}
                    disableError
                  />
                </div> : null}
              </div>
              <div className="resource-wizard__review-summary-records">
                <article className="resource-wizard__review-summary-record">
                  <div className="resource-wizard__review-summary-record-facts">
                    <div className="resource-wizard__info-pair">
                      <span>Auth scheme</span>
                      <strong>{testCredentialType === "current" ? "Current signed-in user" : testCredentialType === "pat" ? "Personal Access Token bearer" : "OAuth bearer token"}</strong>
                    </div>
                    <div className="resource-wizard__info-pair">
                      <span>Route</span>
                      <strong>{routeUrl}</strong>
                    </div>
                    <div className="resource-wizard__info-pair">
                      <span>Route type</span>
                      <strong>{routeModeLabel}</strong>
                    </div>
                    <div className="resource-wizard__info-pair">
                      <span>Discovery checks</span>
                      <strong>Initialize, tools/list, resources/list</strong>
                    </div>
                  </div>
                </article>
              </div>
            </section>
          )}
          {activeAuthTab !== "test" && routeMode !== "client" ? (
            <section className="resource-wizard__review-summary-section">
              <ResourceStateControl compact tone="warning" title="Privileged route" message="Admin and dev/sysadmin route access is managed by privileged backend permissions." />
            </section>
          ) : null}
        </div>
      </div>
      <DialogControl
        openDialog={Boolean(revokePat)}
        onCancel={() => setRevokePat(null)}
        onOk={async () => {
          const tokenId = getString(revokePat, "TokenId", "tokenId");
          setRevokePat(null);
          return onRevokePat?.(tokenId);
        }}
        title="Revoke Active PAT"
        okText={busyKey?.startsWith("revokeServerPat:") ? "Revoking..." : "Revoke"}
        okType="danger"
        cancelText="Cancel"
        disableOk={busyKey?.startsWith("revokeServerPat:")}
        disableCancel={busyKey?.startsWith("revokeServerPat:")}
        fallbackErrorMessage="The personal access token could not be revoked."
      >
        <div className="client-mcp-tools-result-dialog">
          <p>
            Revoke active PAT {getString(revokePat, "TokenLabel", "tokenLabel") || "this token"}? It will immediately stop authenticating and cannot be reactivated. Create a new PAT if access is needed again.
          </p>
        </div>
      </DialogControl>
      <DialogControl
        openDialog={Boolean(deletePat)}
        onCancel={() => setDeletePat(null)}
        onOk={async () => {
          const deleted = await onDeletePat?.(getString(deletePat, "TokenId", "tokenId"));
          if (deleted) setDeletePat(null);
          return deleted;
        }}
        title="Delete Revoked PAT"
        okText={busyKey?.startsWith("deleteServerPat:") ? "Deleting..." : "Delete"}
        okType="danger"
        cancelText="Cancel"
        disableOk={busyKey?.startsWith("deleteServerPat:")}
        disableCancel={busyKey?.startsWith("deleteServerPat:")}
        fallbackErrorMessage="The revoked personal access token could not be deleted."
      >
        <div className="client-mcp-tools-result-dialog">
          <p>
            Delete revoked PAT {getString(deletePat, "TokenLabel", "tokenLabel") || "this token"} from this screen? The audit record will be retained, but the PAT will no longer appear here.
          </p>
        </div>
      </DialogControl>
      <DialogControl
        openDialog={authModeInfoOpen}
        onCancel={() => setAuthModeInfoOpen(false)}
        onOk={() => setAuthModeInfoOpen(false)}
        title="About Authentication Mode"
        okText="Close"
        hideCancel
        hideCancelButton
      >
        <div className="client-mcp-tools-result-dialog">
          <p>
            Authentication identifies the client user. Client Tools and every Data Context still apply their own access policy to every discovery and execution request.
          </p>
          <dl className="resource-workspace-facts">
            <Fact label="Selected mode" value={formatClientServerAuthenticationMode(authenticationMode)} />
            <Fact label="Identity boundary" value="Client and user profile" />
            <Fact label="Authorization boundary" value="Capability and Data Context" />
          </dl>
        </div>
      </DialogControl>
      <DialogControl
        openDialog={entraSetupDialogOpen}
        onCancel={() => setEntraSetupDialogOpen(false)}
        onOk={() => {
          setEntraSetupDialogOpen(false);
          onDownloadEntraSetupScript?.();
        }}
        title="Download Entra Setup Script"
        okText="Download"
        cancelText="Cancel"
        disableOk={downloadingScript}
        disableCancel={downloadingScript}
      >
        <div className="client-mcp-tools-result-dialog">
          <p>
            This generated PowerShell script prepares the Microsoft Entra application used by this Client MCP Server OAuth flow. It adds or verifies the MCP redirect URI and delegated API scope so hosted AI clients can complete sign-in.
          </p>
          <p>
            Downloading only creates the script. It does not change Microsoft Entra until an authorized administrator reviews and runs it against the intended tenant.
          </p>
          <dl className="resource-workspace-facts">
            <Fact label="Target" value="This Client MCP Server" />
            <Fact label="Sign-in" value="Microsoft Graph device code" />
            <Fact label="Required permission" value="Application.ReadWrite.All" />
          </dl>
        </div>
      </DialogControl>
    </div>
  );
};

const ResourceListSummary = ({
  title,
  items,
  labelKey = "name",
  countLabel = "entry",
  emptyTitle = "No entries exposed",
  emptyMessage = "Discovery loaded successfully. This route does not currently expose entries for this section."
}) => {
  const rows = Array.isArray(items) ? items : [];
  const buildAccessLabels = (item) => {
    const flags = getArray(item, "flags", "Flags");
    const hasFlag = (flag) => flags.some((value) => String(value).toLowerCase() === flag.toLowerCase());
    const canChange = getBoolean(item, "changeAllowed", "ChangeAllowed") || hasFlag("ClientChange") || hasFlag("AdminChange");
    const canRead = getBoolean(item, "readAllowed", "ReadAllowed") || canChange || hasFlag("ClientRead") || hasFlag("AdminRead");
    const canExecute = getBoolean(item, "executeAllowed", "ExecuteAllowed") || hasFlag("ClientExecute") || hasFlag("AdminExecute");

    if (canExecute) return ["Execute"];
    if (canChange) return ["Read / Change"];
    if (canRead) return ["Read only"];
    return [];
  };

  const buildRow = (item, index) => {
    const technicalName = getString(item, labelKey, "Name", "name", "itemName", "operationName", "routeName");
    const displayName = getString(item, "displayName", "DisplayName", "label", "Label");
    const titleText = displayName || technicalName || "Unnamed";
    const description = getString(item, "description", "Description", "details", "Details", "routeName", "classification") || "Discovery metadata";
    const accessLabels = buildAccessLabels(item);
    return {
      key: technicalName || titleText || index,
      titleText,
      description,
      accessLabels
    };
  };

  return (
    <section className="resource-workspace-panel">
      <div className="resource-workspace-section-title">
        <h2>{title}</h2>
        <p>{rows.length.toLocaleString()} exposed {countLabel}(s) reported by discovery.</p>
      </div>
      {rows.length === 0 ? (
        <ResourceStateControl compact title={emptyTitle} message={emptyMessage} />
      ) : (
        <div className="resource-workspace-record-list">
          {rows.slice(0, 30).map((item, index) => {
            const row = buildRow(item, index);
            return (
              <article key={row.key} className="resource-workspace-record-row client-mcp-tools-exposure-row">
                <div className="resource-workspace-record-row__copy">
                  <strong>{row.titleText}</strong>
                  <span>{row.description}</span>
                </div>
                {row.accessLabels.length > 0 ? (
                  <div className="resource-workspace-record-row-actions">
                    {row.accessLabels.map((label) => (
                      <span key={label} className="resource-wizard__review-summary-record-status">
                        {label}
                      </span>
                    ))}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

const MetadataCards = ({ title, description, items, nameKeys = ["name", "uri"], detailKeys = ["description", "mimeType"] }) => {
  const rows = Array.isArray(items) ? items : [];
  return (
    <section className="resource-workspace-panel">
      <div className="resource-workspace-section-title">
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      {rows.length === 0 ? (
        <ResourceStateControl compact tone="warning" title="No entries loaded" message="Refresh this screen to load MCP discovery metadata." />
      ) : (
        <div className="resource-workspace-record-list">
          {rows.map((item, index) => (
            <article key={getString(item, ...nameKeys) || index} className="resource-workspace-record-row">
              <div className="resource-workspace-record-row__copy">
                <strong>{getString(item, ...nameKeys) || "Unnamed"}</strong>
                <span>{getString(item, ...detailKeys) || "MCP metadata"}</span>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

const formatRuleLabel = (value) =>
  String(value || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (letter) => letter.toUpperCase());

const RULE_LABELS = {
  "Metadata / Item Metadata Property": "Item exposure setting",
  "Metadata / Operation Metadata Property": "Operation exposure setting",
  "Metadata / Status": "Rule set",
  "Route / Mode": "Route mode",
  "Route / Client Short Name": "Client short name",
  "Route / Is Privileged": "Privileged route"
};

const RULE_VALUES = {
  "UIMetadataItem.McpExposureFlags": "Item metadata controls read, query, schema, save, and delete access for this MCP route.",
  "OperationType.McpExposureFlags": "Operation metadata controls which operations can execute through this MCP route.",
  "generic-item-operation-tools": "Generic item and operation tool rules are active for this route.",
  true: "Yes",
  false: "No"
};

const formatRuleDisplayLabel = (label) => RULE_LABELS[label] || label;

const formatRuleDisplayValue = (value) => {
  const raw = String(value);
  if (Object.prototype.hasOwnProperty.call(RULE_VALUES, raw)) return RULE_VALUES[raw];
  return raw
    .replace(/\bClientRead\b/g, "client item read")
    .replace(/\bClientChange\b/g, "client item change")
    .replace(/\bClientExecute\b/g, "client operation execute")
    .replace(/\bAdminRead\b/g, "admin item read")
    .replace(/\bAdminChange\b/g, "admin item change")
    .replace(/\bAdminExecute\b/g, "admin operation execute")
    .replace(/\bMcpExposureFlags\b/g, "MCP exposure settings")
    .replace(/\bsave_item\b/g, "save item")
    .replace(/\bdelete_item\b/g, "delete item")
    .replace(/\brun_operation\b/g, "run operation");
};

const isRuleScalar = (value) => value === null || ["string", "number", "boolean"].includes(typeof value);

const summarizeRuleValue = (value) => {
  if (value === null || value === undefined) return "None";
  if (Array.isArray(value)) return `${value.length.toLocaleString()} item(s)`;
  if (typeof value === "object") return `${Object.keys(value).length.toLocaleString()} field(s)`;
  return String(value);
};

const buildRuleRows = (payload) => {
  if (!payload || typeof payload !== "object") return [];
  const rows = [];
  Object.entries(payload).forEach(([key, value]) => {
    const label = formatRuleLabel(key);
    if (isRuleScalar(value)) {
      rows.push({ label, value: summarizeRuleValue(value) });
      return;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        rows.push({ label, value: "0 item(s)" });
        return;
      }
      value.forEach((item, index) => {
        rows.push({
          label: `${label} ${index + 1}`,
          value: isRuleScalar(item) ? summarizeRuleValue(item) : summarizeRuleValue(item)
        });
      });
      return;
    }

    const childEntries = Object.entries(value || {});
    const scalarChildren = childEntries.filter(([, childValue]) => isRuleScalar(childValue));
    if (scalarChildren.length === 0) {
      rows.push({ label, value: summarizeRuleValue(value) });
      return;
    }
    scalarChildren.forEach(([childKey, childValue]) => {
      rows.push({ label: `${label} / ${formatRuleLabel(childKey)}`, value: summarizeRuleValue(childValue) });
    });
  });
  return rows;
};

const RuleSummaryCard = ({ title, payload }) => {
  const rulePayload = payload && typeof payload === "object" ? payload : {};
  const rows = buildRuleRows(rulePayload).slice(0, 30);

  return (
    <section className="resource-workspace-panel">
      <div className="resource-workspace-section-title">
        <h2>{title}</h2>
        <p>MCP rule metadata loaded from the Tools route.</p>
      </div>
      {rows.length === 0 ? (
        <ResourceStateControl compact tone="warning" title="Not loaded" message="Refresh this screen to load this MCP rule resource." />
      ) : (
        <div className="resource-workspace-record-list">
          {rows.map((row, index) => (
            <article key={`${row.label}-${index}`} className="resource-workspace-record-row">
              <div className="resource-workspace-record-row__copy">
                <strong>{formatRuleDisplayLabel(row.label)}</strong>
                <span>{formatRuleDisplayValue(row.value)}</span>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

const ResourceConfigurationClientMcpToolsScreen = ({
  selectedResource,
  configuration,
  resourceConfigurations = [],
  screenKey,
  onRefreshConfigurations,
  onScreenActionsChange
}) => {
  const { coreContext, managers } = useResourceDomainContext();
  const itemServiceFactory = useMemo(() => new ItemServiceFactory(), []);
  const aiSessionManager = useGenericManager(itemServiceFactory.ItemServiceEnum.AIChatSession);
  const routeMode = resolveRouteMode({ configuration, selectedResource });
  const isClientMcpServer = isClientMcpServerResource(configuration, selectedResource);
  const routeModeLabel = getRouteModeLabel(routeMode, isClientMcpServer);
  const clientShortName = resolveClientShortName({ coreContext, configuration, selectedResource });
  const route = buildToolsRoute(routeMode, clientShortName);
  const routeUrl = buildToolsUrl(routeMode, clientShortName);
  const connectorUrls = useMemo(
    () => buildClientMcpConnectorUrls(routeMode, clientShortName, isClientMcpServer),
    [clientShortName, isClientMcpServer, routeMode]
  );
  const serverName = isClientMcpServer ? CLIENT_SERVER_NAME : CLIENT_TOOLS_SERVER_NAME;
  const resourceConfigurationId = getId(configuration);
  const clientResourceId = getId(selectedResource?.clientResource) || getId(selectedResource);
  const authenticationTabStorageKey = resourceConfigurationId && clientResourceId
    ? `${CLIENT_MCP_AUTHENTICATION_TAB_STORAGE_PREFIX}:${clientResourceId}:${resourceConfigurationId}`
    : "";
  const routeReady = routeMode === "client"
    ? Boolean(clientShortName && !routeUrl.includes("{clientShortName}"))
    : Boolean(routeUrl);
  const routeEnabledRaw = getCustomField(
    configuration,
    isClientMcpServer ? "ClientMcpServerEnabled" : "McpToolsClientEnabled",
    routeMode === "admin" ? "McpToolsAdminEnabled" : "",
    routeMode === "sysadmin" ? "McpToolsDevSysAdminEnabled" : "",
    isClientMcpServer ? "" : "McpToolsRouteEnabled"
  );
  const routeEnabled = String(routeEnabledRaw || "true").toLowerCase() !== "false";
  const [busyKey, setBusyKey] = useState("");
  const [dialog, setDialog] = useState({ open: false, title: "", status: "", details: null, onClose: null });
  const patSecretDialogResult = useMemo(() => {
    if (dialog.details?.type !== "pat-secret") return { copyValue: "", html: "" };
    const parsed = extractPatSecretDialogResult(dialog.details?.html);
    return {
      copyValue: getString(dialog.details, "copyValue") || parsed.copyValue,
      html: parsed.html
    };
  }, [dialog.details]);
  const testedToolNames = getArray(dialog.details, "tools").map((name) => String(name || ""));
  const clientToolsEnabledForProfile = testedToolNames.some((name) => name.startsWith("client_"));
  const dataQueryEnabledForProfile = testedToolNames.some((name) => name.startsWith("data_"))
    || getArray(dialog.details, "resources").some((uri) => String(uri || "").includes("/data-contexts/"));
  const [lastCheckedUtc, setLastCheckedUtc] = useState("");
  const [responses, setResponses] = useState({});
  const [screenLoadState, setScreenLoadState] = useState({});
  const [oauthScopes, setOauthScopes] = useState(() => String(getCustomField(configuration, "ClientMcpServerOAuthScopes") || "").trim());
  const [serverAuthenticationMode, setServerAuthenticationMode] = useState(() => normalizeClientServerAuthenticationMode(
    getCustomField(configuration, "ClientMcpServerAuthenticationMode")
  ));
  const [serverPatLabel, setServerPatLabel] = useState("");
  const [serverPatExpiration, setServerPatExpiration] = useState("1m");
  const serverPersonalAccessTokens = useMemo(
    () => getArray(configuration, "ClientMcpServerPersonalAccessTokens", "clientMcpServerPersonalAccessTokens"),
    [configuration]
  );
  const [accessPanelOpen, setAccessPanelOpen] = useState(false);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiPanelSession, setAiPanelSession] = useState(null);
  const [aiPanelChats, setAiPanelChats] = useState([]);
  const [copiedGuidePromptTitle, setCopiedGuidePromptTitle] = useState("");
  const [serverProviderState, setServerProviderState] = useState({ loading: false, rows: [] });
  const [activeSessionWorkspace, setActiveSessionWorkspace] = useState(null);
  const [activeSessionSearch, setActiveSessionSearch] = useState("");
  const [activeSessionStatusFilter, setActiveSessionStatusFilter] = useState("Active");
  const [activeSessionOnOrBefore, setActiveSessionOnOrBefore] = useState(getDefaultActiveSessionOnOrBefore);
  const [selectedActiveSession, setSelectedActiveSession] = useState(null);
  const [activeSessionLoading, setActiveSessionLoading] = useState(false);
  const [activeSessionLoadingMore, setActiveSessionLoadingMore] = useState(false);
  const [activeSessionError, setActiveSessionError] = useState("");
  const [activeSessionGeneration, setActiveSessionGeneration] = useState(0);
  const [killSessionDialogOpen, setKillSessionDialogOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadServerProviders = async () => {
      if (!isClientMcpServer || screenKey !== MCP_TOOLS_OVERVIEW_SCREEN_KEY || !resourceConfigurationId) {
        setServerProviderState({ loading: false, rows: [] });
        return;
      }

      const providerConfigurations = resourceConfigurations.filter((providerConfiguration) =>
        isLinkedToClientMcpServer(providerConfiguration, resourceConfigurationId)
        && (isClientMcpToolsProvider(providerConfiguration) || isMcpQueryDataProvider(providerConfiguration))
      );
      setServerProviderState({ loading: true, rows: [] });

      const rows = await Promise.all(providerConfigurations.map(async (providerConfiguration) => {
        if (isClientMcpToolsProvider(providerConfiguration)) {
          const configurationStatus = getConfigurationStatusLabel(providerConfiguration);
          const clientEnabled = String(getCustomField(providerConfiguration, "McpToolsClientEnabled") || "true").toLowerCase() !== "false";
          const routeEnabled = String(getCustomField(providerConfiguration, "McpToolsRouteEnabled") || "true").toLowerCase() !== "false";
          const enabled = configurationStatus !== "Disabled"
            && configurationStatus !== "Retired"
            && clientEnabled
            && routeEnabled;
          return {
            key: getId(providerConfiguration) || "client-mcp-tools",
            title: "Client MCP: Tools",
            description: "Paid Client Tools capability",
            status: enabled ? "Enabled" : "Disabled"
          };
        }

        const schemaApiRecord = getLinkedSchemaApiRecord(providerConfiguration);
        const schemaApiId = getString(schemaApiRecord, "ItemId", "itemId");
        let schemaApiItem = null;
        if (schemaApiId && managers?.schemaAPI?.getItem) {
          try {
            const response = await managers.schemaAPI.getItem(schemaApiId);
            schemaApiItem = getItems(response)[0] || null;
          } catch (ex) {
            schemaApiItem = null;
          }
        }

        const configurationStatus = getConfigurationStatusLabel(providerConfiguration);
        const schemaApiActiveValue = getValue(schemaApiItem, "IsActive", "isActive");
        const deployments = getArray(schemaApiItem, "Deployments", "deployments")
          .filter((deployment) => !getBoolean(deployment, "IsDeleted", "isDeleted"))
          .filter((deployment) => getValue(deployment, "IsActive", "isActive") === undefined
            || getBoolean(deployment, "IsActive", "isActive"));
        const deploymentStatuses = deployments.map((deployment) => getSchemaDestinationStatusLabel(
          getValue(deployment, "SchemaDestinationStatus", "schemaDestinationStatus")
        ));
        let status = configurationStatus;
        if (["Disabled", "Retired"].includes(configurationStatus)
          || (schemaApiItem && schemaApiActiveValue !== undefined && !getBoolean(schemaApiItem, "IsActive", "isActive"))) {
          status = "Disabled";
        } else if (deploymentStatuses.includes("Online")) {
          status = "Online";
        } else if (deploymentStatuses.includes("Offline")) {
          status = "Offline";
        } else if (deployments.length > 0) {
          status = "Unavailable";
        } else if (schemaApiId && !schemaApiItem) {
          status = "Unavailable";
        }

        const displayName = getString(schemaApiItem, "DisplayName", "displayName")
          || getString(schemaApiRecord, "DisplayName", "displayName")
          || getString(providerConfiguration, "DisplayName", "displayName")
          || "Query Data endpoint";
        const apiName = getString(schemaApiItem, "APIName", "apiName");
        return {
          key: getId(providerConfiguration) || schemaApiId || displayName,
          title: "Client MCP: Query Data",
          description: apiName && normalizeCompare(apiName) !== normalizeCompare(displayName)
            ? `${displayName} (${apiName})`
            : displayName,
          status
        };
      }));

      if (!cancelled) {
        setServerProviderState({
          loading: false,
          rows: rows.sort((left, right) => {
            if (left.title === "Client MCP: Tools" && right.title !== "Client MCP: Tools") return -1;
            if (right.title === "Client MCP: Tools" && left.title !== "Client MCP: Tools") return 1;
            return `${left.title} ${left.description}`.localeCompare(`${right.title} ${right.description}`);
          })
        });
      }
    };

    loadServerProviders();
    return () => {
      cancelled = true;
    };
  }, [isClientMcpServer, managers?.schemaAPI, resourceConfigurationId, resourceConfigurations, screenKey]);

  useEffect(() => {
    setAiPanelSession(null);
    setAiPanelChats([]);
  }, [resourceConfigurationId]);

  useEffect(() => {
    setResponses({});
    setScreenLoadState({});
    setLastCheckedUtc("");
  }, [routeUrl]);

  const loadActiveSessions = useCallback(async ({
    page = 0,
    append = false,
    searchText = activeSessionSearch,
    statusFilter = activeSessionStatusFilter,
    onOrBefore = activeSessionOnOrBefore
  } = {}) => {
    if (!isClientMcpServer || !resourceConfigurationId) return null;
    if (statusFilter === "Inactive" && !String(onOrBefore || "").trim()) {
      setSelectedActiveSession(null);
      setActiveSessionError("");
      setActiveSessionLoading(false);
      setActiveSessionLoadingMore(false);
      setActiveSessionWorkspace({
        Page: 0,
        PageSize: 50,
        TotalCount: 0,
        HasMore: false,
        Sessions: []
      });
      return null;
    }
    if (append) setActiveSessionLoadingMore(true);
    else {
      setActiveSessionLoading(true);
      setSelectedActiveSession(null);
      setActiveSessionGeneration((value) => value + 1);
    }
    setActiveSessionError("");
    try {
      const result = await managers?.resourceConfiguration?.RunOperation?.({
        operationName: LOAD_CLIENT_MCP_SERVER_ACTIVE_SESSIONS_OPERATION,
        id: resourceConfigurationId,
        item: configuration,
        isItem: true,
        queryParams: {
          clientResourceId,
          resourceConfigurationId,
          searchText,
          statusFilter,
          onOrBeforeUtc: onOrBeforeDateToUtc(onOrBefore),
          page,
          pageSize: 50
        }
      });
      if (getValue(result, "Success", "success") === false) {
        throw new Error(getOperationMessage(
          result,
          "Unable to load active Client MCP Server sessions."
        ));
      }
      const workspace = getOperationResultItem(result);
      if (!workspace) throw new Error("The active session inventory was not returned.");
      const nextSessions = getArray(workspace, "Sessions", "sessions");
      setActiveSessionWorkspace((current) => ({
        ...workspace,
        Sessions: append
          ? [...getArray(current, "Sessions", "sessions"), ...nextSessions]
          : nextSessions
      }));
      return workspace;
    } catch (ex) {
      setActiveSessionError(ex?.message || "Unable to load active Client MCP Server sessions.");
      return null;
    } finally {
      setActiveSessionLoading(false);
      setActiveSessionLoadingMore(false);
    }
  }, [
    activeSessionOnOrBefore,
    activeSessionSearch,
    activeSessionStatusFilter,
    clientResourceId,
    configuration,
    isClientMcpServer,
    managers?.resourceConfiguration,
    resourceConfigurationId
  ]);

  const searchActiveSessions = useCallback((searchText) => {
    const nextSearch = String(searchText || "").trim();
    setActiveSessionSearch(nextSearch);
  }, []);

  const revokeActiveSession = useCallback(async () => {
    const sessionId = activeSessionId(selectedActiveSession);
    const sessionKind = activeSessionKind(selectedActiveSession);
    if (!sessionId || !sessionKind || !activeSessionIsActive(selectedActiveSession)) {
      return { Success: false };
    }
    setBusyKey("revokeActiveSession");
    setActiveSessionError("");
    try {
      const result = await managers?.resourceConfiguration?.RunOperation?.({
        operationName: REVOKE_CLIENT_MCP_SERVER_ACTIVE_SESSION_OPERATION,
        id: resourceConfigurationId,
        item: configuration,
        isItem: true,
        isWarning: true,
        queryParams: {
          clientResourceId,
          resourceConfigurationId,
          sessionId,
          sessionKind,
          searchText: activeSessionSearch,
          statusFilter: activeSessionStatusFilter,
          onOrBeforeUtc: onOrBeforeDateToUtc(activeSessionOnOrBefore),
          page: 0,
          pageSize: 50
        }
      });
      if (getValue(result, "Success", "success") === false) {
        throw new Error(getOperationMessage(
          result,
          "Unable to end the selected Client MCP Server session."
        ));
      }
      const workspace = getOperationResultItem(result);
      if (!workspace) throw new Error("The ended-session result was not returned.");
      setActiveSessionWorkspace(workspace);
      setSelectedActiveSession(null);
      setActiveSessionGeneration((value) => value + 1);
      setKillSessionDialogOpen(false);
      return result;
    } catch (ex) {
      const message = ex?.message || "Unable to end the selected Client MCP Server session.";
      if (/no longer available/i.test(message)) {
        setSelectedActiveSession(null);
        setActiveSessionGeneration((value) => value + 1);
      }
      setActiveSessionError(message);
      return { Success: false, DisplayMessage: message };
    } finally {
      setBusyKey("");
    }
  }, [
    activeSessionOnOrBefore,
    activeSessionSearch,
    activeSessionStatusFilter,
    clientResourceId,
    configuration,
    managers?.resourceConfiguration,
    resourceConfigurationId,
    selectedActiveSession
  ]);

  useEffect(() => {
    if (screenKey === MCP_TOOLS_ACTIVE_SESSIONS_SCREEN_KEY && isClientMcpServer) {
      loadActiveSessions({ page: 0 });
    }
  }, [isClientMcpServer, loadActiveSessions, resourceConfigurationId, screenKey]);

  useEffect(() => {
    setOauthScopes(String(getCustomField(configuration, "ClientMcpServerOAuthScopes") || "").trim());
    setServerAuthenticationMode(normalizeClientServerAuthenticationMode(
      getCustomField(configuration, "ClientMcpServerAuthenticationMode")
    ));
  }, [configuration]);

  const openAccessPanel = useCallback(() => {
    setAccessPanelOpen(true);
  }, []);

  const closeAccessPanel = useCallback(() => {
    setAccessPanelOpen(false);
  }, []);

  useEffect(() => {
    if (isClientMcpServer || screenKey !== MCP_TOOLS_OVERVIEW_SCREEN_KEY) {
      setAccessPanelOpen(false);
    }
  }, [isClientMcpServer, screenKey]);

  const showResultDialog = useCallback((title, status, details, onClose = null) => {
    setDialog({ open: true, title, status, details, onClose });
  }, []);

  const closeResultDialog = useCallback(() => {
    const onClose = dialog.onClose;
    setDialog({ open: false, title: "", status: "", details: null, onClose: null });
    onClose?.();
  }, [dialog.onClose]);

  const loadAiPanelSession = useCallback(async () => {
    if (!resourceConfigurationId || !aiSessionManager?.Query) {
      setAiPanelSession(null);
      setAiPanelChats([]);
      return null;
    }

    try {
      const result = await aiSessionManager.Query({
        model: "x => x.IsActive == true && x.IsDeleted == false && x.ReferenceId == referenceId",
        params: { referenceId: resourceConfigurationId }
      });
      const session = getItems(result)
        .filter((item) => getString(item, "ReferenceItemName", "referenceItemName") === AI_QUESTION_CLIENT_MCP_TOOLS_REFERENCE)
        .sort((left, right) => getSortDateValue(right) - getSortDateValue(left))[0] || null;

      setAiPanelSession(session);
      setAiPanelChats([]);
      return session;
    } catch (ex) {
      setAiPanelSession(null);
      setAiPanelChats([]);
      return null;
    }
  }, [aiSessionManager, resourceConfigurationId]);

  const openAiPanel = useCallback(() => {
    void loadAiPanelSession();
    setAiPanelOpen(true);
  }, [loadAiPanelSession]);

  const closeAiPanel = useCallback(() => {
    setAiPanelOpen(false);
  }, []);

  const handleAiSessionChange = useCallback((sessionId, session, chats) => {
    if (session) {
      setAiPanelSession(session);
    } else if (sessionId) {
      setAiPanelSession((current) => current || { _id: sessionId, ReferenceId: resourceConfigurationId, ReferenceItemName: AI_QUESTION_CLIENT_MCP_TOOLS_REFERENCE });
    }
    if (Array.isArray(chats)) setAiPanelChats(chats);
  }, [resourceConfigurationId]);

  const copyGuidePrompt = useCallback(async (promptItem) => {
    const promptText = getString(promptItem, "prompt");
    const promptTitle = getString(promptItem, "title");
    if (!promptText) return;
    try {
      await navigator.clipboard?.writeText(promptText);
      setCopiedGuidePromptTitle(promptTitle);
      window.setTimeout(() => setCopiedGuidePromptTitle(""), 1600);
    } catch (ex) {
      showResultDialog("Guide", promptText, null);
    }
  }, [showResultDialog]);

  const callMcp = useCallback(async (key, label, method, params, options = {}) => {
    if (!routeReady) throw new Error(routeMode === "client" ? "Client short name is required before calling the MCP route." : "MCP tools route is not available.");
    const token = String(options.accessToken || getAccessToken() || "").trim();
    if (!token) throw new Error("A bearer credential is required.");
    const showDialog = options.showDialog !== false;
    const trackBusy = options.trackBusy !== false;
    if (trackBusy) setBusyKey(key);
    try {
      const response = await fetch(routeUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...(options.bypassDiscoveryCache === true ? { "X-Convoke-Mcp-Discovery-Cache": "bypass" } : {})
        },
        body: JSON.stringify(buildRpcPayload(method, params))
      });
      const payload = await readRpcResponse(response);
      setResponses((current) => ({ ...current, [key]: payload }));
      setLastCheckedUtc(new Date().toISOString());
      if (showDialog) {
        showResultDialog(label, options.successMessage || "Request completed.", options.dialogType ? { ...payload, type: options.dialogType, routeUrl } : payload);
      }
      return payload;
    } catch (ex) {
      if (showDialog) showResultDialog(label, ex?.message || "Request failed.", { error: ex?.message || String(ex) });
      throw ex;
    } finally {
      if (trackBusy) setBusyKey("");
    }
  }, [routeMode, routeReady, routeUrl, showResultDialog]);

  const copyUrl = useCallback(() => {
    showResultDialog("MCP URLs", "Choose the connector for your AI client.", { connectorUrls, type: "mcp-url" });
  }, [connectorUrls, showResultDialog]);

  const copyDialogUrl = useCallback(async (url, label) => {
    if (!url) return;
    try {
      await navigator.clipboard?.writeText(url);
      setDialog((current) => ({ ...current, status: `${label} URL copied.` }));
    } catch (ex) {
      setDialog((current) => ({ ...current, status: `Copy the ${label} URL from the field below.` }));
    }
  }, []);

  const copyPatSecret = useCallback(async (secret) => {
    if (!secret) return;
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard access is unavailable.");
      await navigator.clipboard.writeText(secret);
      setDialog((current) => ({
        ...current,
        details: {
          ...current.details,
          patSecretCopied: true,
          patSecretCopyError: ""
        }
      }));
    } catch (ex) {
      setDialog((current) => ({
        ...current,
        details: {
          ...current.details,
          patSecretCopied: false,
          patSecretCopyError: "Clipboard copy failed. Select the PAT secret above and copy it manually."
        }
      }));
    }
  }, []);

  const initialize = useCallback((options) => callMcp("initialize", "Validate", "initialize", {
    protocolVersion: PROTOCOL_VERSION,
    capabilities: {},
    clientInfo: { name: "convoke-platformapp-resource-workspace", version: "1.0" }
  }, { ...options, dialogType: "initialize", successMessage: "The MCP connection has been validated." }), [callMcp]);
  const validateInstallationReadiness = useCallback(async () => {
    if (routeMode !== "client") {
      await initialize();
      return;
    }
    if (!routeReady) {
      showResultDialog("Validate", "Validation failed.", {
        type: "installation-readiness-error",
        message: "The client short name is required before the Client MCP Server can be installed."
      });
      return;
    }

    setBusyKey("initialize");
    try {
      const checkedIssuers = new Set();
      for (const connector of connectorUrls) {
        const connectorUrl = getString(connector, "url");
        const connectorLabel = getString(connector, "label") || "MCP connector";
        const connectorUri = new URL(connectorUrl, window.location.origin);
        const guideResponse = await fetch(connectorUrl, {
          method: "GET",
          headers: { Accept: "text/markdown" }
        });
        if (!guideResponse.ok) {
          throw new Error(`${connectorLabel} setup guide returned HTTP ${guideResponse.status}.`);
        }
        const guideContentType = String(guideResponse.headers?.get?.("content-type") || "").toLowerCase();
        const guide = await guideResponse.text();
        if (!guideContentType.includes("text/markdown") || !/Client MCP Server/i.test(guide)) {
          throw new Error(`${connectorLabel} did not return the public Client MCP Server setup guide.`);
        }

        const protectedResourceUrl = `${connectorUri.origin}/.well-known/oauth-protected-resource${connectorUri.pathname}`;
        const protectedResourceResponse = await fetch(protectedResourceUrl, {
          method: "GET",
          headers: { Accept: "application/json" }
        });
        if (!protectedResourceResponse.ok) {
          throw new Error(`${connectorLabel} OAuth discovery returned HTTP ${protectedResourceResponse.status}.`);
        }
        const protectedResource = JSON.parse(await protectedResourceResponse.text());
        const authorizationServers = getArray(protectedResource, "authorization_servers", "authorizationServers");
        if (getString(protectedResource, "resource") !== connectorUrl || authorizationServers.length === 0) {
          throw new Error(`${connectorLabel} OAuth discovery does not identify the selected connector.`);
        }

        for (const issuer of authorizationServers) {
          if (checkedIssuers.has(issuer)) continue;
          const issuerUri = new URL(issuer, connectorUri.origin);
          const metadataUrl = `${issuerUri.origin}/.well-known/oauth-authorization-server${issuerUri.pathname}`;
          const metadataResponse = await fetch(metadataUrl, {
            method: "GET",
            headers: { Accept: "application/json" }
          });
          if (!metadataResponse.ok) {
            throw new Error(`OAuth authorization metadata returned HTTP ${metadataResponse.status}.`);
          }
          const metadata = JSON.parse(await metadataResponse.text());
          if (getString(metadata, "issuer") !== issuer || !getString(metadata, "authorization_endpoint", "authorizationEndpoint") || !getString(metadata, "token_endpoint", "tokenEndpoint")) {
            throw new Error("OAuth authorization metadata is incomplete.");
          }
          checkedIssuers.add(issuer);
        }

        const challengeResponse = await fetch(connectorUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(buildRpcPayload("initialize", {
            protocolVersion: PROTOCOL_VERSION,
            capabilities: {},
            clientInfo: { name: "convoke-platformapp-readiness-check", version: "1.0" }
          }))
        });
        if (challengeResponse.status !== 401) {
          throw new Error(`${connectorLabel} did not return the expected authentication challenge.`);
        }
      }

      setLastCheckedUtc(new Date().toISOString());
      showResultDialog("Validate", "Ready to install.", {
        type: "installation-readiness",
        connectors: connectorUrls
      });
    } catch (ex) {
      showResultDialog("Validate", "Validation failed.", {
        type: "installation-readiness-error",
        message: ex?.message || "The Client MCP Server is not ready to install."
      });
    } finally {
      setBusyKey("");
    }
  }, [connectorUrls, initialize, routeMode, routeReady, showResultDialog]);
  const loadCurrentUserDiscoverySnapshot = useCallback(async () => {
    const result = await managers?.resourceConfiguration?.RunOperation?.({
      operationName: TEST_CLIENT_MCP_SERVER_CURRENT_USER_CONNECTION_OPERATION,
      id: resourceConfigurationId,
      item: configuration,
      isItem: true,
      queryParams: {
        clientResourceId,
        resourceConfigurationId
      }
    });
    if (operationResponseFailed(result)) {
      throw new Error(getOperationFailureMessage(
        result,
        "The current signed-in user is not authorized to discover this Client MCP route."
      ));
    }

    const response = getOperationResponse(result);
    const rawSnapshot = getString(response, "Value", "value");
    const snapshot = rawSnapshot ? JSON.parse(rawSnapshot) : null;
    if (!snapshot) {
      throw new Error("The current-user connection operation did not return an MCP discovery snapshot.");
    }

    const capabilities = getValue(snapshot, "capabilities", "Capabilities") || {};
    const tools = getArray(snapshot, "tools", "Tools")
      .map((tool) => typeof tool === "string" ? { name: tool } : tool)
      .filter(Boolean);
    const resources = getArray(snapshot, "resources", "Resources")
      .map((resource) => typeof resource === "string" ? { uri: resource } : resource)
      .filter(Boolean);

    setResponses((current) => ({
      ...current,
      autoLoadError: undefined,
      tools: { result: { tools } },
      resources: { result: { resources } },
      capabilities: { result: capabilities },
      accessMatrix: { result: getValue(capabilities, "accessRules", "AccessRules") || {} },
      changeSafety: { result: getValue(capabilities, "mutationRules", "MutationRules") || {} }
    }));
    setLastCheckedUtc(new Date().toISOString());
    return { snapshot, capabilities, tools, resources };
  }, [clientResourceId, configuration, managers?.resourceConfiguration, resourceConfigurationId]);

  const testAccess = useCallback(async () => {
    setBusyKey("testAccess");
    try {
      await loadCurrentUserDiscoverySnapshot();
      showResultDialog("Test Access", "Success. The current user can connect to the Client MCP Tools route.", null);
    } catch (ex) {
      const message = `Failed. ${ex?.message || "The current user could not connect to the Client MCP Tools route."}`;
      showResultDialog("Test Access", message, { type: "test-access-error", message });
    } finally {
      setBusyKey("");
    }
  }, [loadCurrentUserDiscoverySnapshot, showResultDialog]);

  const markScreenLoad = useCallback((key, status, error = "") => {
    setScreenLoadState((current) => ({
      ...current,
      [key]: { status, error }
    }));
  }, []);

  const loadCapabilities = useCallback(async () => {
    markScreenLoad(MCP_TOOLS_CAPABILITIES_SCREEN_KEY, "loading");
    setResponses((current) => ({
      ...current,
      autoLoadError: undefined,
      tools: undefined,
      resources: undefined,
      capabilities: undefined
    }));
    try {
      await loadCurrentUserDiscoverySnapshot();
      markScreenLoad(MCP_TOOLS_CAPABILITIES_SCREEN_KEY, "loaded");
    } catch (ex) {
      markScreenLoad(MCP_TOOLS_CAPABILITIES_SCREEN_KEY, "failed", ex?.message || String(ex));
    }
  }, [loadCurrentUserDiscoverySnapshot, markScreenLoad]);

  const loadRules = useCallback(async () => {
    markScreenLoad(MCP_TOOLS_RULES_SCREEN_KEY, "loading");
    setResponses((current) => ({
      ...current,
      autoLoadError: undefined,
      accessMatrix: undefined,
      changeSafety: undefined
    }));
    try {
      await loadCurrentUserDiscoverySnapshot();
      markScreenLoad(MCP_TOOLS_RULES_SCREEN_KEY, "loaded");
    } catch (ex) {
      markScreenLoad(MCP_TOOLS_RULES_SCREEN_KEY, "failed", ex?.message || String(ex));
    }
  }, [loadCurrentUserDiscoverySnapshot, markScreenLoad]);

  const loadExposure = useCallback(async () => {
    markScreenLoad(MCP_TOOLS_EXPOSURE_SCREEN_KEY, "loading");
    setResponses((current) => ({
      ...current,
      autoLoadError: undefined,
      capabilities: undefined
    }));
    try {
      await loadCurrentUserDiscoverySnapshot();
      markScreenLoad(MCP_TOOLS_EXPOSURE_SCREEN_KEY, "loaded");
    } catch (ex) {
      markScreenLoad(MCP_TOOLS_EXPOSURE_SCREEN_KEY, "failed", ex?.message || String(ex));
    }
  }, [loadCurrentUserDiscoverySnapshot, markScreenLoad]);

  useEffect(() => {
    if (!routeReady || !routeEnabled) return;
    const loadStateKey = screenKey === MCP_TOOLS_OVERVIEW_SCREEN_KEY
      ? MCP_TOOLS_CAPABILITIES_SCREEN_KEY
      : screenKey;
    const currentLoadState = screenLoadState[loadStateKey]?.status || "idle";
    if (currentLoadState !== "idle") return;
    if (screenKey === MCP_TOOLS_OVERVIEW_SCREEN_KEY && !isClientMcpServer) loadCapabilities();
    if (screenKey === MCP_TOOLS_RULES_SCREEN_KEY) loadRules();
    if (screenKey === MCP_TOOLS_EXPOSURE_SCREEN_KEY) loadExposure();
  }, [
    loadCapabilities,
    loadExposure,
    loadRules,
    isClientMcpServer,
    routeEnabled,
    routeReady,
    screenKey,
    screenLoadState
  ]);

  const saveServerAuthentication = useCallback(async ({ showDialog = true, refresh = true } = {}) => {
    if (!isClientMcpServer) return false;
    setBusyKey("saveServerAuthentication");
    try {
      const customFields = {
        ...getCustomFields(configuration),
        ClientMcpServerEnabled: routeEnabled,
        ClientMcpServerAuthenticationMode: serverAuthenticationMode,
        ClientMcpServerOAuthScopes: oauthScopes.trim()
      };
      const result = await managers?.resourceConfiguration?.RunOperation?.({
        operationName: SAVE_CLIENT_MCP_SERVER_SETTINGS_OPERATION,
        id: getId(configuration),
        item: { ...configuration, CustomFields: customFields, customFields },
        isItem: true,
        queryParams: {
          clientResourceId: selectedResource?.id || getString(configuration, "ClientResourceId", "clientResourceId"),
          resourceConfigurationId: getId(configuration)
        }
      });
      if (showDialog) showResultDialog("Save Authentication", "Client MCP Server authentication settings saved.", result);
      if (refresh) onRefreshConfigurations?.();
      return true;
    } catch (ex) {
      if (showDialog) showResultDialog("Save Authentication", ex?.message || "Unable to save Client MCP Server authentication settings.", null);
      return false;
    } finally {
      setBusyKey("");
    }
  }, [configuration, isClientMcpServer, managers?.resourceConfiguration, oauthScopes, onRefreshConfigurations, routeEnabled, selectedResource?.id, serverAuthenticationMode, showResultDialog]);

  const createServerPat = useCallback(async () => {
    if (!isClientMcpServer || !String(serverPatLabel || "").trim()) return;
    if (!clientServerAuthenticationAllowsPat(serverAuthenticationMode)) {
      showResultDialog("Create PAT", "Choose Personal Access Token or OAuth + Personal Access Token first.", null);
      return;
    }

    const saved = await saveServerAuthentication({ showDialog: false, refresh: false });
    if (!saved) {
      showResultDialog("Create PAT", "Authentication settings could not be saved before creating the token.", null);
      return;
    }

    setBusyKey("createServerPat");
    try {
      const result = await managers?.resourceConfiguration?.RunOperation?.({
        operationName: CREATE_CLIENT_MCP_SERVER_PAT_OPERATION,
        id: getId(configuration),
        item: configuration,
        isItem: true,
        queryParams: {
          clientResourceId: selectedResource?.id || getString(configuration, "ClientResourceId", "clientResourceId"),
          resourceConfigurationId: getId(configuration),
          tokenLabel: serverPatLabel.trim(),
          expirationDuration: serverPatExpiration || "1m"
        }
      });
      if (operationResponseFailed(result)) {
        const message = getOperationFailureMessage(
          result,
          "Unable to create the Client MCP Server personal access token."
        );
        showResultDialog(
          "Personal Access Token Could Not Be Created",
          message,
          { type: "server-authentication-error", message }
        );
        return;
      }
      const rawHtml = getString(result, "Value", "value");
      const patSecretResult = extractPatSecretDialogResult(rawHtml);
      const html = patSecretResult.html;
      setServerPatLabel("");
      showResultDialog(
        "Personal Access Token Created",
        "Copy this secret now. It will not be shown again.",
        {
          type: "pat-secret",
          html,
          copyValue: patSecretResult.copyValue,
          result: html ? null : result
        },
        onRefreshConfigurations
      );
    } catch (ex) {
      showResultDialog("Create PAT", ex?.message || "Unable to create the Client MCP Server personal access token.", null);
    } finally {
      setBusyKey("");
    }
  }, [configuration, isClientMcpServer, managers?.resourceConfiguration, onRefreshConfigurations, saveServerAuthentication, selectedResource?.id, serverAuthenticationMode, serverPatExpiration, serverPatLabel, showResultDialog]);

  const revokeServerPat = useCallback(async (tokenId) => {
    if (!isClientMcpServer || !tokenId) return;
    setBusyKey(`revokeServerPat:${tokenId}`);
    try {
      await managers?.resourceConfiguration?.RunOperation?.({
        operationName: REVOKE_CLIENT_MCP_SERVER_PAT_OPERATION,
        id: getId(configuration),
        item: configuration,
        isItem: true,
        queryParams: {
          clientResourceId: selectedResource?.id || getString(configuration, "ClientResourceId", "clientResourceId"),
          resourceConfigurationId: getId(configuration),
          tokenId
        }
      });
      showResultDialog("Revoke PAT", "The personal access token has been revoked.", null);
      onRefreshConfigurations?.();
    } catch (ex) {
      showResultDialog("Revoke PAT", ex?.message || "Unable to revoke the Client MCP Server personal access token.", null);
    } finally {
      setBusyKey("");
    }
  }, [configuration, isClientMcpServer, managers?.resourceConfiguration, onRefreshConfigurations, selectedResource?.id, showResultDialog]);

  const deleteServerPat = useCallback(async (tokenId) => {
    if (!isClientMcpServer || !tokenId) return false;
    setBusyKey(`deleteServerPat:${tokenId}`);
    try {
      const result = await managers?.resourceConfiguration?.RunOperation?.({
        operationName: REVOKE_CLIENT_MCP_SERVER_PAT_OPERATION,
        id: getId(configuration),
        item: configuration,
        isItem: true,
        queryParams: {
          clientResourceId: selectedResource?.id || getString(configuration, "ClientResourceId", "clientResourceId"),
          resourceConfigurationId: getId(configuration),
          tokenId,
          softDelete: true
        }
      });
      if (operationResponseFailed(result)) {
        throw new Error(getOperationFailureMessage(
          result,
          "Unable to delete the revoked Client MCP Server personal access token."
        ));
      }
      onRefreshConfigurations?.();
      return true;
    } catch (ex) {
      throw new Error(ex?.message || "Unable to delete the revoked Client MCP Server personal access token.");
    } finally {
      setBusyKey("");
    }
  }, [configuration, isClientMcpServer, managers?.resourceConfiguration, onRefreshConfigurations, selectedResource?.id]);

  const testServerAuthentication = useCallback(async ({ credentialType, credential }) => {
    if (!isClientMcpServer || (credentialType !== "current" && !String(credential || "").trim())) return;
    setBusyKey("testServerAuthentication");
    try {
      if (credentialType === "current") {
        const { snapshot, tools: toolEntries, resources: resourceEntries } = await loadCurrentUserDiscoverySnapshot();
        const tools = toolEntries.map((item) => getString(item, "name", "Name")).filter(Boolean);
        const resources = resourceEntries.map((item) => getString(item, "uri", "Uri")).filter(Boolean);
        showResultDialog("MCP Connection Test", `Success. This profile can discover ${tools.length} tool${tools.length === 1 ? "" : "s"} and ${resources.length} deployment${resources.length === 1 ? "" : "s"}.`, {
          type: "server-authentication-test",
          credentialType,
          serverInfo: getValue(snapshot, "serverInfo", "ServerInfo"),
          tools,
          resources
        });
        return;
      }

      const options = {
        accessToken: credential,
        showDialog: false,
        trackBusy: false,
        bypassDiscoveryCache: true
      };
      const initializeResult = await callMcp("serverTestInitialize", "Initialize", "initialize", {
        protocolVersion: PROTOCOL_VERSION,
        capabilities: {},
        clientInfo: { name: "convoke-platformapp-authentication-test", version: "1.0" }
      }, options);
      const toolsResult = await callMcp("serverTestTools", "Tools", "tools/list", undefined, options);
      const resourcesResult = await callMcp("serverTestResources", "Resources", "resources/list", undefined, options);
      const tools = getArray(toolsResult?.result, "tools", "Tools");
      const resources = getArray(resourcesResult?.result, "resources", "Resources");
      showResultDialog("MCP Connection Test", `Success. This profile can discover ${tools.length} tool${tools.length === 1 ? "" : "s"} and ${resources.length} deployment${resources.length === 1 ? "" : "s"}.`, {
        type: "server-authentication-test",
        credentialType,
        serverInfo: initializeResult?.result?.serverInfo || initializeResult?.result?.ServerInfo,
        tools: tools.map((item) => getString(item, "name", "Name")).filter(Boolean),
        resources: resources.map((item) => getString(item, "uri", "Uri")).filter(Boolean)
      });
    } catch (ex) {
      const message = ex?.message || "The Client MCP Server connection test failed.";
      showResultDialog("MCP Connection Test", message, {
        type: "server-authentication-error",
        message
      });
    } finally {
      setBusyKey("");
    }
  }, [callMcp, isClientMcpServer, loadCurrentUserDiscoverySnapshot, showResultDialog]);

  const downloadServerOAuthSetupScript = useCallback(async () => {
    if (!isClientMcpServer) return;
    setBusyKey("downloadServerOAuthSetupScript");
    try {
      const result = await managers?.resourceConfiguration?.RunOperation?.({
        operationName: DOWNLOAD_CLIENT_MCP_SERVER_OAUTH_SETUP_SCRIPT_OPERATION,
        id: getId(configuration),
        item: configuration,
        isItem: true,
        queryParams: {
          clientResourceId: selectedResource?.id || getString(configuration, "ClientResourceId", "clientResourceId"),
          resourceConfigurationId: getId(configuration),
          publicBaseUrl: getHostedApiBaseUri()
        }
      });
      const rawHtml = getString(result, "Value", "value");
      const html = rawHtml ? sanitizeHtmlFragment(rawHtml) : "";
      showResultDialog("MCP OAuth Setup Script", "Script is ready.", {
        type: "download-script",
        html,
        result: html ? null : result
      });
      onRefreshConfigurations?.();
    } catch (ex) {
      showResultDialog("MCP OAuth Setup Script", ex?.message || "Unable to prepare the Client MCP Server OAuth setup script.", null);
    } finally {
      setBusyKey("");
    }
  }, [configuration, isClientMcpServer, managers?.resourceConfiguration, onRefreshConfigurations, selectedResource?.id, showResultDialog]);

  const actionDisabled = !routeReady || !routeEnabled || Boolean(busyKey);
  const capabilityDiscoveryBusy = screenLoadState[MCP_TOOLS_CAPABILITIES_SCREEN_KEY]?.status === "loading";
  const rulesDiscoveryBusy = screenLoadState[MCP_TOOLS_RULES_SCREEN_KEY]?.status === "loading";
  const exposureDiscoveryBusy = screenLoadState[MCP_TOOLS_EXPOSURE_SCREEN_KEY]?.status === "loading";
  const refreshOverview = useCallback(async () => {
    const refreshConfiguration = Promise.resolve(onRefreshConfigurations?.());
    const refreshCapabilities = !isClientMcpServer && routeReady && routeEnabled
      ? loadCapabilities({ bypassDiscoveryCache: true })
      : Promise.resolve();
    await Promise.all([refreshConfiguration, refreshCapabilities]);
  }, [isClientMcpServer, loadCapabilities, onRefreshConfigurations, routeEnabled, routeReady]);
  useEffect(() => {
    const overviewActions = [
      ...(!isClientMcpServer ? [{
        key: "refresh",
        label: "Refresh",
        iconKey: "refresh",
        disabled: Boolean(busyKey) || capabilityDiscoveryBusy,
        loading: capabilityDiscoveryBusy,
        onClick: refreshOverview
      }] : []),
      { key: "mcp-copy-url", label: "MCP URLs", iconElement: <EndpointRouteIcon />, disabled: Boolean(busyKey), onClick: copyUrl },
      { key: "mcp-initialize", label: "Validate", iconKey: "success", disabled: actionDisabled, loading: busyKey === "initialize", onClick: validateInstallationReadiness },
      ...(!isClientMcpServer ? [
        { key: "mcp-access", label: "Access", iconKey: "key", disabled: routeMode !== "client" || Boolean(busyKey), onClick: openAccessPanel },
        { key: "mcp-guide", label: "Guide", iconKey: "guide", disabled: !routeReady || Boolean(busyKey), onClick: openAiPanel }
      ] : [])
    ];
    const actionsByScreen = {
      default: overviewActions,
      [MCP_TOOLS_OVERVIEW_SCREEN_KEY]: overviewActions,
      [MCP_TOOLS_AUTH_SCREEN_KEY]: [
        ...(isClientMcpServer ? [{ key: "mcp-save-authentication", label: "Save Authentication", iconKey: "save", disabled: Boolean(busyKey), loading: busyKey === "saveServerAuthentication", onClick: saveServerAuthentication }] : [
          { key: "mcp-test-access", label: "Test Access", iconKey: "settings_ethernet", disabled: actionDisabled, loading: busyKey === "testAccess", onClick: testAccess }
        ])
      ],
      [MCP_TOOLS_ACTIVE_SESSIONS_SCREEN_KEY]: [
        {
          key: "refresh",
          label: "Refresh",
          iconKey: "refresh",
          disabled: activeSessionLoading || Boolean(busyKey),
          loading: activeSessionLoading,
          onClick: () => loadActiveSessions({
            page: 0,
            searchText: activeSessionSearch,
            statusFilter: activeSessionStatusFilter,
            onOrBefore: activeSessionOnOrBefore
          })
        },
        {
          key: "kill-session",
          label: "Kill Session",
          iconKey: "delete",
          danger: true,
          disabled: !selectedActiveSession ||
            !activeSessionIsActive(selectedActiveSession) ||
            Boolean(busyKey),
          loading: busyKey === "revokeActiveSession",
          onClick: () => setKillSessionDialogOpen(true)
        }
      ],
      [MCP_TOOLS_RULES_SCREEN_KEY]: [
        { key: "refresh", label: "Refresh", iconKey: "refresh", disabled: actionDisabled || rulesDiscoveryBusy, loading: rulesDiscoveryBusy, onClick: () => loadRules({ bypassDiscoveryCache: true }) }
      ],
      [MCP_TOOLS_EXPOSURE_SCREEN_KEY]: [
        { key: "refresh", label: "Refresh", iconKey: "refresh", disabled: actionDisabled || exposureDiscoveryBusy, loading: exposureDiscoveryBusy, onClick: () => loadExposure({ bypassDiscoveryCache: true }) }
      ]
    };
    onScreenActionsChange?.(actionsByScreen[screenKey] || actionsByScreen.default);
    return () => onScreenActionsChange?.([]);
  }, [
    activeSessionOnOrBefore,
    actionDisabled,
    activeSessionSearch,
    activeSessionStatusFilter,
    activeSessionLoading,
    busyKey,
    capabilityDiscoveryBusy,
    copyUrl,
    exposureDiscoveryBusy,
    initialize,
    isClientMcpServer,
    loadCapabilities,
    loadActiveSessions,
    loadExposure,
    loadRules,
    onScreenActionsChange,
    openAccessPanel,
    openAiPanel,
    refreshOverview,
    routeEnabled,
    routeMode,
    rulesDiscoveryBusy,
    saveServerAuthentication,
    selectedActiveSession,
    downloadServerOAuthSetupScript,
    screenKey,
    testAccess,
    validateInstallationReadiness
  ]);

  const capabilityPayload = useMemo(() => readCapabilityPayload(responses), [responses]);
  const exposedItems = capabilityPayload?.exposedItems || capabilityPayload?.ExposedItems || [];
  const exposedOperations = capabilityPayload?.exposedOperations || capabilityPayload?.ExposedOperations || [];
  const capabilityTools = (capabilityPayload?.tools || []).map((item) => (typeof item === "string" ? { name: item } : item));
  const capabilityResources = (capabilityPayload?.resources || []).map((item) => (typeof item === "string" ? { uri: item } : item));
  const discoveredTools = responses.tools?.result?.tools || capabilityTools;
  const metadataUris = responses.resources?.result?.resources || capabilityResources;
  const accessMatrixPayload = readResourcePayload(responses.accessMatrix);
  const changeSafetyPayload = readResourcePayload(responses.changeSafety);
  const accessMatrixLoaded = hasRulePayload(accessMatrixPayload);
  const changeSafetyLoaded = hasRulePayload(changeSafetyPayload);
  const toolCount = responses.tools?.result?.tools?.length ?? capabilityPayload?.tools?.length ?? 0;
  const metadataUriCount = responses.resources?.result?.resources?.length ?? capabilityPayload?.resources?.length ?? 0;
  const lastCheckedDisplay = formatDateTime(lastCheckedUtc);
  const capabilitiesLoadState = screenLoadState[MCP_TOOLS_CAPABILITIES_SCREEN_KEY] || EMPTY_LOAD_STATE;
  const rulesLoadState = screenLoadState[MCP_TOOLS_RULES_SCREEN_KEY] || EMPTY_LOAD_STATE;
  const exposureLoadState = screenLoadState[MCP_TOOLS_EXPOSURE_SCREEN_KEY] || EMPTY_LOAD_STATE;
  const capabilitiesPendingInitialLoad = routeReady
    && routeEnabled
    && !isClientMcpServer
    && screenKey === MCP_TOOLS_OVERVIEW_SCREEN_KEY
    && capabilitiesLoadState.status === "idle";
  const rulesPendingInitialLoad = routeReady && routeEnabled && screenKey === MCP_TOOLS_RULES_SCREEN_KEY && rulesLoadState.status === "idle";
  const exposurePendingInitialLoad = routeReady && routeEnabled && screenKey === MCP_TOOLS_EXPOSURE_SCREEN_KEY && exposureLoadState.status === "idle";
  const capabilitiesLoading = capabilitiesLoadState.status === "loading" || capabilitiesPendingInitialLoad;
  const rulesLoading = rulesLoadState.status === "loading" || rulesPendingInitialLoad;
  const exposureLoading = exposureLoadState.status === "loading" || exposurePendingInitialLoad;
  const rulesLoaded = rulesLoadState.status === "loaded";
  const exposureLoaded = exposureLoadState.status === "loaded";

  const overview = (
    <div className="resource-workspace-overview resource-workspace-configuration-screen client-mcp-tools-manager" style={{ alignContent: "start" }}>
      <section className="resource-workspace-panel resource-workspace-linked-item-section">
        <div className="resource-workspace-section-title">
          <h2>{routeModeLabel}</h2>
          {!isClientMcpServer ? <p>Paid Convoke tools capability linked to the shared Client MCP Server.</p> : null}
        </div>
        <dl className="resource-workspace-facts resource-workspace-linked-item-metrics">
          <Fact label="Route mode" value={routeMode} />
          <Fact label="Client short name" value={routeMode === "client" ? clientShortName : "Not applicable"} />
          <Fact label="Route" value={`GET|POST ${route}`} />
          <Fact label="Enabled" value={routeEnabled ? "Enabled" : "Disabled"} />
          <Fact label="Server" value={serverName} />
          <Fact label="MCP protocol" value={PROTOCOL_VERSION} />
          {!isClientMcpServer ? <Fact label="Tools" value={toolCount.toLocaleString()} /> : null}
          {!isClientMcpServer ? <Fact label="MCP metadata URIs" value={metadataUriCount.toLocaleString()} /> : null}
          {!isClientMcpServer ? <Fact label="Last checked" value={lastCheckedDisplay} /> : null}
        </dl>
        {isClientMcpServer ? (
          <div className="client-mcp-server-provider-list">
            <div className="resource-workspace-section-title">
              <h3>Available MCP capabilities</h3>
              <p>Tools and Query Data endpoints currently linked to this Client MCP Server.</p>
            </div>
            {serverProviderState.loading ? (
              <ResourceStateControl loading compact title="Loading capabilities" message="Checking linked Client MCP capabilities and endpoint status." />
            ) : serverProviderState.rows.length === 0 ? (
              <ResourceStateControl compact tone="warning" title="No capabilities available" message="No Client MCP Tools or Query Data endpoints are linked to this server." />
            ) : (
              <div className="resource-workspace-record-list">
                {serverProviderState.rows.map((provider) => (
                  <article className="resource-workspace-record-row" key={provider.key}>
                    <div className="resource-workspace-record-row__copy">
                      <strong>{provider.title}</strong>
                      <span>{provider.description}</span>
                    </div>
                    <span className={`resource-workspace-status-pill ${getProviderStatusClassName(provider.status)}`}>
                      {provider.status}
                    </span>
                  </article>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </section>
      {!isClientMcpServer && screenKey === MCP_TOOLS_OVERVIEW_SCREEN_KEY && capabilitiesLoading ? (
        <ResourceStateControl loading compact title="Loading discovery" message="Loading MCP tools, metadata URIs, and capability summary for this route." />
      ) : null}
      {!isClientMcpServer && screenKey === MCP_TOOLS_OVERVIEW_SCREEN_KEY && capabilitiesLoadState.status === "failed" ? (
        <ResourceStateControl compact tone="warning" title="Discovery load failed" message={capabilitiesLoadState.error} />
      ) : null}
      {!isClientMcpServer && screenKey === MCP_TOOLS_OVERVIEW_SCREEN_KEY ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 24rem), 1fr))", gap: "1rem", alignItems: "start" }}>
          <MetadataCards
            title="MCP Tools"
            description="Tools currently advertised to AI clients by this Client MCP route."
            items={discoveredTools}
            nameKeys={["name", "Name"]}
            detailKeys={["description", "Description"]}
          />
          <MetadataCards
            title="MCP Metadata URIs"
            description="MCP resource URIs that AI clients can read for route metadata."
            items={metadataUris}
            nameKeys={["uri", "Uri", "name", "Name"]}
            detailKeys={["description", "Description", "mimeType", "MimeType"]}
          />
        </div>
      ) : null}
    </div>
  );

  const authScreen = isClientMcpServer ? (
    <ClientMcpToolsAuthenticationControl
      downloadingScript={busyKey === "downloadServerOAuthSetupScript"}
      authenticationMode={serverAuthenticationMode}
      busyKey={busyKey}
      onDownloadEntraSetupScript={downloadServerOAuthSetupScript}
      onCreatePat={createServerPat}
      onDeletePat={deleteServerPat}
      onRevokePat={revokeServerPat}
      onTestConnection={testServerAuthentication}
      oauthScopes={oauthScopes}
      patExpiration={serverPatExpiration}
      patLabel={serverPatLabel}
      personalAccessTokens={serverPersonalAccessTokens}
      routeEnabled={routeEnabled}
      routeMode={routeMode}
      routeModeLabel={routeModeLabel}
      routeReady={routeReady}
      routeUrl={routeUrl}
      setAuthenticationMode={setServerAuthenticationMode}
      setOauthScopes={setOauthScopes}
      setPatExpiration={setServerPatExpiration}
      setPatLabel={setServerPatLabel}
      tabStorageKey={authenticationTabStorageKey}
    />
  ) : null;

  const activeSessionsScreen = isClientMcpServer ? (
    <div className="resource-workspace-overview client-mcp-tools-manager" style={{ alignContent: "start" }}>
      <section className="resource-workspace-panel">
        <div className="resource-workspace-section-title">
          <h2>Active Sessions</h2>
          <p>
            Inspect valid OAuth connections and personal access tokens for this Client MCP Server. Active means the credential can authenticate; it does not indicate that the MCP client is online now.
          </p>
        </div>
        <ActiveSessionListControl
          sessions={getArray(activeSessionWorkspace, "Sessions", "sessions")}
          selectedSession={selectedActiveSession}
          onSelectedSessionChange={setSelectedActiveSession}
          searchText={activeSessionSearch}
          onSearchChange={searchActiveSessions}
          statusFilter={activeSessionStatusFilter}
          onStatusFilterChange={(value) => {
            setSelectedActiveSession(null);
            setActiveSessionStatusFilter(value);
            setActiveSessionOnOrBefore(value === "Inactive"
              ? getDefaultInactiveSessionOnOrBefore()
              : getDefaultActiveSessionOnOrBefore());
          }}
          onOrBefore={activeSessionOnOrBefore}
          onOnOrBeforeChange={(value) => {
            setSelectedActiveSession(null);
            setActiveSessionOnOrBefore(value);
          }}
          loading={activeSessionLoading}
          loadingMore={activeSessionLoadingMore}
          error={activeSessionError}
          hasMore={getBoolean(activeSessionWorkspace, "HasMore", "hasMore")}
          resultGeneration={activeSessionGeneration}
          onLoadMore={() => loadActiveSessions({
            page: Number(getValue(activeSessionWorkspace, "Page", "page") || 0) + 1,
            append: true,
            searchText: activeSessionSearch,
            statusFilter: activeSessionStatusFilter,
            onOrBefore: activeSessionOnOrBefore
          })}
          emptyMessage={activeSessionStatusFilter === "Inactive" && !activeSessionOnOrBefore
            ? "Choose an On or before date to load inactive OAuth connections and personal access tokens."
            : activeSessionStatusFilter === "Inactive"
              ? "No inactive OAuth connections or personal access tokens match these filters."
            : "No active OAuth connections or personal access tokens were found for this Client MCP Server."}
        />
      </section>
      <DialogControl
        openDialog={killSessionDialogOpen}
        onCancel={() => setKillSessionDialogOpen(false)}
        onOk={revokeActiveSession}
        title="Kill Active Session"
        okText={busyKey === "revokeActiveSession" ? "Ending..." : "Kill Session"}
        okType="danger"
        cancelText="Cancel"
        disableOk={busyKey === "revokeActiveSession"}
        disableCancel={busyKey === "revokeActiveSession"}
      >
        <div className="client-mcp-tools-result-dialog">
          <p>
            End the selected {getString(selectedActiveSession, "CredentialType", "credentialType") || "authentication"} session? Killing a personal-access-token session does not revoke the PAT; using the active PAT again creates a new session.
          </p>
          <ActiveSessionDetails session={selectedActiveSession} />
        </div>
      </DialogControl>
    </div>
  ) : null;

  const rulesScreen = (
    <div className="resource-workspace-overview client-mcp-tools-manager" style={{ alignContent: "start" }}>
      <section className="resource-workspace-panel">
        <div className="resource-workspace-section-title">
          <h2>Rules</h2>
          <p>Read access and change safety metadata reported by the MCP discovery surface.</p>
        </div>
        <dl className="resource-workspace-facts">
          <Fact label="Access matrix" value={accessMatrixLoaded ? "Loaded" : "Not loaded"} />
          <Fact label="Change safety" value={changeSafetyLoaded ? "Loaded" : "Not loaded"} />
          <Fact label="Last checked" value={lastCheckedDisplay} />
        </dl>
      </section>
      {rulesLoading ? (
        <ResourceStateControl loading compact title="Loading rules" message="Loading access matrix and change safety metadata for this route." />
      ) : null}
      {rulesLoadState.status === "failed" ? (
        <ResourceStateControl compact tone="warning" title="Rules load failed" message={rulesLoadState.error} />
      ) : null}
      {rulesLoaded ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 24rem), 1fr))", gap: "1rem", alignItems: "start" }}>
          <RuleSummaryCard title="Access Matrix" payload={accessMatrixPayload} />
          <RuleSummaryCard title="Change Safety" payload={changeSafetyPayload} />
        </div>
      ) : null}
    </div>
  );

  const exposureScreen = (
    <div className="resource-workspace-overview client-mcp-tools-manager" style={{ alignContent: "start" }}>
      <section className="resource-workspace-panel">
        <div className="resource-workspace-section-title">
          <h2>Exposed Metadata</h2>
          <p>Items and operations currently advertised by convoke://capabilities for this route.</p>
        </div>
        <dl className="resource-workspace-facts">
          <Fact label="Load status" value={exposureLoaded ? "Loaded" : exposureLoading ? "Loading" : "Not loaded"} />
          <Fact label="Exposed items" value={exposedItems.length.toLocaleString()} />
          <Fact label="Exposed operations" value={exposedOperations.length.toLocaleString()} />
          <Fact label="Last checked" value={lastCheckedDisplay} />
        </dl>
      </section>
      {exposureLoading ? (
        <ResourceStateControl loading compact title="Loading exposed metadata" message="Loading exposed items and operations from route capabilities." />
      ) : null}
      {exposureLoadState.status === "failed" ? (
        <ResourceStateControl compact tone="warning" title="Exposed metadata load failed" message={exposureLoadState.error} />
      ) : null}
      {exposureLoaded ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 24rem), 1fr))", gap: "1rem", alignItems: "start" }}>
          <ResourceListSummary
            title="Exposed Items"
            items={exposedItems}
            labelKey="itemName"
            countLabel="item"
            emptyTitle="No items exposed"
            emptyMessage="Discovery loaded successfully. Add MCP item exposure metadata to a Convoke item for it to appear here."
          />
          <ResourceListSummary
            title="Exposed Operations"
            items={exposedOperations}
            labelKey="operationName"
            countLabel="operation"
            emptyTitle="No operations exposed"
            emptyMessage="Discovery loaded successfully. Add MCP operation exposure metadata to a Convoke operation for it to appear here."
          />
        </div>
      ) : null}
    </div>
  );

  return (
    <>
      {screenKey === MCP_TOOLS_AUTH_SCREEN_KEY ? authScreen : null}
      {screenKey === MCP_TOOLS_ACTIVE_SESSIONS_SCREEN_KEY ? activeSessionsScreen : null}
      {screenKey === MCP_TOOLS_RULES_SCREEN_KEY ? rulesScreen : null}
      {screenKey === MCP_TOOLS_EXPOSURE_SCREEN_KEY ? exposureScreen : null}
      {[
        MCP_TOOLS_AUTH_SCREEN_KEY,
        MCP_TOOLS_ACTIVE_SESSIONS_SCREEN_KEY,
        MCP_TOOLS_RULES_SCREEN_KEY,
        MCP_TOOLS_EXPOSURE_SCREEN_KEY
      ].includes(screenKey) ? null : overview}
      {!isClientMcpServer ? (
        <PanelControl
          open={Boolean(accessPanelOpen && routeMode === "client")}
          title="MCP Access: Client MCP Tools"
          onClose={closeAccessPanel}
          mapToClass="core-layout"
          width={760}
          minWidth={480}
          maxWidth={960}
          storageKey="resource-workspace-client-mcp-tools-access-panel-width"
          className="resource-workspace-endpoint-authentication-panel"
          contentClassName="resource-workspace-endpoint-authentication-panel__content"
        >
          <div className="resource-workspace-endpoint-authentication-panel__layout">
            <div className="resource-workspace-endpoint-authentication-panel__body">
              <div className="resource-wizard__linked-step" style={{ alignSelf: "start", minHeight: "auto" }}>
                <div className="resource-wizard__linked-step-header">
                  <div>
                    <h2>MCP Access</h2>
                    <p>Review and test the permission boundary for the Client Tools capability.</p>
                  </div>
                </div>
                <ResourceStateControl
                  compact
                  title="Permission controlled"
                  message="Access requires an active signed-in user in this client and the Client MCP Tools Product Resource permission. Resource Administrator role membership supplies that permission; profile lists do not grant or deny access."
                />
                <section className="resource-wizard__review-summary-section">
                  <div className="resource-wizard__review-summary-section-header">
                    <div>
                      <h4>MCP Access Test</h4>
                      <p className="resource-wizard__review-summary-record-subtitle">
                        Test the exact active-client and Product Resource permission boundary using the current signed-in user.
                      </p>
                    </div>
                    <div className="resource-wizard__linked-step-actions-right">
                      <ButtonControl
                        type="ok-small"
                        disabled={Boolean(busyKey)}
                        loading={busyKey === "testAccess"}
                        onClick={testAccess}
                      >
                        Test Access
                      </ButtonControl>
                    </div>
                  </div>
                  <div className="resource-wizard__review-summary-records">
                    <article className="resource-wizard__review-summary-record">
                      <div className="resource-wizard__review-summary-record-facts">
                        <div className="resource-wizard__info-pair">
                          <span>Route</span>
                          <strong>{routeUrl}</strong>
                        </div>
                        <div className="resource-wizard__info-pair">
                          <span>Client boundary</span>
                          <strong>Exact active client</strong>
                        </div>
                        <div className="resource-wizard__info-pair">
                          <span>Access policy</span>
                          <strong>Client MCP Tools Resource permission</strong>
                        </div>
                      </div>
                    </article>
                  </div>
                </section>
                </div>
              </div>
            <footer className="resource-workspace-endpoint-authentication-panel__footer">
              <ButtonControl
                type="ok"
                className="resource-workspace-endpoint-authentication-panel__footer-button"
                disabled={Boolean(busyKey)}
                onClick={closeAccessPanel}
              >
                Close
              </ButtonControl>
            </footer>
          </div>
        </PanelControl>
      ) : null}
      <DialogControl
        openDialog={dialog.open}
        onCancel={closeResultDialog}
        onOk={closeResultDialog}
        title={dialog.title || routeModeLabel}
        okText="Close"
        hideCancel
        hideCancelButton
      >
        <div className="client-mcp-tools-result-dialog">
          {["initialize", "installation-readiness", "installation-readiness-error", "test-access-error", "server-authentication-test", "server-authentication-error", "pat-secret"].includes(dialog.details?.type)
            ? null
            : <p>{dialog.status}</p>}
          {dialog.details?.type === "mcp-url" ? (
            <div className="client-mcp-tools-url-dialog">
              {getArray(dialog.details, "connectorUrls").map((connector) => (
                <div className="client-mcp-tools-url-row" key={getString(connector, "key", "url")}>
                  <div className="client-mcp-tools-url-copy">
                    <span className="client-mcp-tools-url-heading">
                      <strong>{getString(connector, "label")}</strong>
                    </span>
                    <strong>{getString(connector, "url")}</strong>
                  </div>
                  <ButtonControl
                    type="standard-small"
                    title={`Copy ${getString(connector, "label")} URL`}
                    aria-label={`Copy ${getString(connector, "label")} URL`}
                    onClick={() => copyDialogUrl(getString(connector, "url"), getString(connector, "label"))}
                  >
                    <IconControl iconKey="contenttab" width="1rem" height="1rem" />
                  </ButtonControl>
                </div>
              ))}
              <p>Use the connector that matches your AI client. All three connect to the same shared Client MCP Server and expose the capabilities authorized for the signed-in user.</p>
            </div>
          ) : dialog.details?.type === "initialize" ? (
            renderInitializeDialog(dialog.details, serverName)
          ) : dialog.details?.type === "installation-readiness" ? (
            renderInstallationReadinessDialog(dialog.details)
          ) : dialog.details?.type === "installation-readiness-error" ? (
            <ResourceStateControl
              compact
              tone="warning"
              title="Not ready to install"
              message={getString(dialog.details, "message") || "The Client MCP Server did not pass its public installation-readiness checks."}
            />
          ) : dialog.details?.type === "test-access-error" ? (
            <ConvokeDialogMessage
              message={getString(dialog.details, "message") || "Failed. The current user could not connect to the Client MCP Tools route."}
              tone="error"
            />
          ) : dialog.details?.type === "download-script" ? (
            dialog.details.html ? (
              <div dangerouslySetInnerHTML={{ __html: dialog.details.html }} />
            ) : dialog.details.result ? (
              <pre>{prettyJson(dialog.details.result)}</pre>
            ) : null
          ) : dialog.details?.type === "pat-secret" ? (
            dialog.details.html ? (
              <div className="client-mcp-tools-pat-result">
                <div
                  className="client-mcp-tools-pat-result__content"
                  dangerouslySetInnerHTML={{ __html: patSecretDialogResult.html }}
                />
                {patSecretDialogResult.copyValue ? (
                  <div className="client-mcp-tools-pat-result__actions">
                    <ButtonControl
                      type="standard-small"
                      title="Copy PAT Secret"
                      aria-label="Copy PAT Secret"
                      onClick={() => copyPatSecret(patSecretDialogResult.copyValue)}
                    >
                      <IconControl iconKey="contenttab" width="1rem" height="1rem" />
                      <span>{dialog.details?.patSecretCopied ? "Copied" : "Copy PAT Secret"}</span>
                    </ButtonControl>
                    {dialog.details?.patSecretCopyError ? (
                      <span className="client-mcp-tools-pat-result__copy-error" role="alert">
                        {dialog.details.patSecretCopyError}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : dialog.details.result ? (
              <pre>{prettyJson(dialog.details.result)}</pre>
            ) : null
          ) : dialog.details?.type === "server-authentication-error" ? (
            <ConvokeDialogMessage
              message={getString(dialog.details, "message") || "The Client MCP Server rejected the connection."}
              tone="error"
            />
          ) : dialog.details?.type === "server-authentication-test" ? (
            <div className="client-mcp-tools-authentication-test-result">
              <div
                className="resource-workspace-access-note resource-workspace-access-note--success"
                role="status"
              >
                {dialog.status}
              </div>
              <section className="client-mcp-tools-authentication-capability-section" aria-label="Capability availability">
                <h3>Enabled capabilities</h3>
                <div className="client-mcp-tools-authentication-capability-grid">
                  <article className="client-mcp-tools-authentication-capability-item">
                    <span>
                      <strong>Client MCP: Tools</strong>
                      <small>Client operations available to this profile.</small>
                    </span>
                    <span className={`resource-workspace-status-pill ${clientToolsEnabledForProfile ? "resource-workspace-status-pill--success" : "resource-workspace-status-pill--muted"}`}>
                      {clientToolsEnabledForProfile ? "Enabled" : "Not enabled"}
                    </span>
                  </article>
                  <article className="client-mcp-tools-authentication-capability-item">
                    <span>
                      <strong>Client MCP: Query Data</strong>
                      <small>Data Query tools and deployments available to this profile.</small>
                    </span>
                    <span className={`resource-workspace-status-pill ${dataQueryEnabledForProfile ? "resource-workspace-status-pill--success" : "resource-workspace-status-pill--muted"}`}>
                      {dataQueryEnabledForProfile ? "Enabled" : "Not enabled"}
                    </span>
                  </article>
                </div>
              </section>
              <div className="client-mcp-tools-authentication-result-grid">
                <section className="client-mcp-tools-authentication-result-section">
                  <div className="client-mcp-tools-authentication-result-heading">
                    <h3>Authorized tools</h3>
                    <span>{`${getArray(dialog.details, "tools").length} ${getArray(dialog.details, "tools").length === 1 ? "tool" : "tools"}`}</span>
                  </div>
                  {getArray(dialog.details, "tools").length ? (
                    <div className="client-mcp-tools-authentication-tool-groups">
                      {groupAuthorizedMcpTools(getArray(dialog.details, "tools")).map((group) => (
                        <section
                          className="client-mcp-tools-authentication-tool-group"
                          aria-label={group.label}
                          key={group.key}
                        >
                          <div className="client-mcp-tools-authentication-tool-group-heading">
                            <h4>{group.label}</h4>
                            <span>{`${group.tools.length} ${group.tools.length === 1 ? "tool" : "tools"}`}</span>
                          </div>
                          <ul className="client-mcp-tools-authentication-result-list client-mcp-tools-authentication-result-list--tools">
                            {group.tools.map((name) => <li key={name}><code>{name}</code></li>)}
                          </ul>
                        </section>
                      ))}
                    </div>
                  ) : <p className="client-mcp-tools-authentication-result-empty">No tools are authorized for this profile.</p>}
                </section>
                <section className="client-mcp-tools-authentication-result-section">
                  <div className="client-mcp-tools-authentication-result-heading">
                    <h3>Authorized deployments</h3>
                    <span>{`${getArray(dialog.details, "resources").length} ${getArray(dialog.details, "resources").length === 1 ? "deployment" : "deployments"}`}</span>
                  </div>
                  {getArray(dialog.details, "resources").length ? (
                    <ul className="client-mcp-tools-authentication-result-list">
                      {getArray(dialog.details, "resources").map((uri) => <li key={uri}><code>{uri}</code></li>)}
                    </ul>
                  ) : <p className="client-mcp-tools-authentication-result-empty">No deployments are authorized for this profile.</p>}
                </section>
              </div>
            </div>
          ) : dialog.details ? <pre>{prettyJson(dialog.details)}</pre> : null}
        </div>
      </DialogControl>
      {!isClientMcpServer ? <AIPanel
        open={aiPanelOpen}
        presentation="slideout"
        mapToClass="core-layout"
        title={`${routeModeLabel} Assistant`}
        className="client-mcp-tools-ai-panel"
        onCancel={closeAiPanel}
        onSessionChange={handleAiSessionChange}
        operationName={AI_QUESTION_CLIENT_MCP_TOOLS_OPERATION}
        referenceId={resourceConfigurationId}
        referenceName={AI_QUESTION_CLIENT_MCP_TOOLS_REFERENCE}
        params={{
          resourceConfigurationId,
          clientResourceId,
          routeMode,
          routeUrl,
          clientShortName
        }}
        payload={{
          resourceConfigurationId,
          clientResourceId,
          routeMode,
          routeUrl,
          clientShortName,
          serverName,
          protocolVersion: PROTOCOL_VERSION
        }}
        session={aiPanelSession}
        aiChats={aiPanelChats}
        chatPane="right"
        chatPaneCollapseMode="open"
        chatPaneStartOpen={true}
        directModeOnly
        aiProcessingMode="Direct"
        singleSessionMode
        planningPreviewEnabled={false}
        previewPanelEnabled={false}
        splashMessage={`Ask about this ${routeModeLabel}.`}
        introMessage="Ask how to connect an AI client, which capabilities are available, what metadata is exposed, or how to use this MCP route safely."
        minPanelWidth={720}
        minLeft={320}
        minRight={420}
      >
        <ClientMcpToolsGuidePanel
          routeUrl={routeUrl}
          routeEnabled={routeEnabled}
          routeMode={routeMode}
          clientShortName={clientShortName}
          toolCount={toolCount}
          metadataUriCount={metadataUriCount}
          exposedItems={exposedItems}
          exposedOperations={exposedOperations}
          lastCheckedDisplay={lastCheckedDisplay}
          copiedPromptTitle={copiedGuidePromptTitle}
          onCopyPrompt={copyGuidePrompt}
        />
      </AIPanel> : null}
    </>
  );
};

export default ResourceConfigurationClientMcpToolsScreen;
